/**
 * Data loaders and mutation actions for the client: the async `load*`/`open*`
 * functions that fill the signals, the AI-chat SSE subscription and turn loop,
 * intake extraction polling, and the per-action helpers wired from the root
 * delegates. Holds no screen markup and never imports the screens/shell, so it
 * stays a lower layer the rest of the app depends on (app.tsx split, HS2-ZB7GJN).
 */
import { closeDialog } from '@kerfjs/ui/surface-scaffold';
import { effect } from 'kerfjs';

import { isFinishedAnalysisStatus } from '../../shared/analysis-status.js';
import type { AnalysisRowT } from '../../shared/contract/analysis.js';
import type { RecordDecision } from '../../shared/contract/decisions.js';
import {
  formAcceptsAnswers,
  type FormAnswer,
  type FormAnswerResponse,
  type FormSkipResponse,
} from '../../shared/contract/forms.js';
import type { Profile, Thresholds } from '../../shared/contract/orgs.js';
import {
  DEMAND_NOTES_SCOPE,
  type DemandNote,
  noteScopeKey,
  notesInScope,
  unreadNotes,
} from '../../shared/demand-notes.js';
import { DECISION_ACTIONS, type DecisionAction } from '../../shared/enums.js';
import { effectiveFxRates, isCurrencyCode } from '../../shared/fx-rates.js';
import { actingReadOnly } from '../lib/acting-read-only.js';
import { analysesNeedReload } from '../lib/analysis-sync.js';
import type { RecommendedBatchDecision } from '../lib/batch-decision.js';
import { canAssignDemands, canOpenDemandSegment, currentDemandId, intakeCtx, isRequester } from '../lib/context.js';
import { orderedCurrencyCodes } from '../lib/currencies.js';
import { currencyRatesWithChange } from '../lib/currency-settings.js';
import {
  DEMAND_PAGE_SIZE,
  DEMAND_RELOAD_MAX,
  demandListFilterQuery,
  demandListRequestQuery,
} from '../lib/demand-list-query.js';
import { elChecked, elValue, field, numField, selectValue } from '../lib/dom.js';
import { isImageDocument } from '../lib/entity-reference.js';
import { interactionGeneration, transientInteractionOpen } from '../lib/interaction-generation.js';
import { EMPTY_MATRIX_SELECTION } from '../lib/matrix-selection.js';
import { loadString, removePref, saveString } from '../lib/prefs.js';
import { savingsPeriodDays } from '../lib/savings-period.js';
import { showToast } from '../lib/toast.js';
import { type AnalysisProgressEvent, applyAnalysisProgress } from './analysis-progress.js';
import { waitForTerminalAnalysis } from './analysis-wait.js';
import type { ChatTarget, DemandListResponse } from './api.js';
import { api } from './api.js';
import { takeShellBootstrap } from './bootstrap.js';
import { startDemandChangePoll } from './demand-change-poll.js';
import { createDemandTaskGuard, type DemandTask, newTerminalAnalysis } from './demand-task-guard.js';
import { connectConversationEvents, connectDemandEvents, type DemandEventStream } from './events.js';
import { buildManualQuotationInput } from './manual-quotation-input.js';
import { type MultifileBatch, multifileBatchResult } from './multifile-fast-path.js';
import { createQuoteMatrixLoader, type QuoteMatrixLoadOptions } from './quote-matrix-loader.js';
import {
  aiBudgetDialogOpen,
  aiUsage,
  aiUsageLoadState,
  analysisList,
  analysisProgress,
  analysisReport,
  analysisRunning,
  assignableMembers,
  assumptionFxCodes,
  assumptionsDialog,
  attachmentErrorsOpen,
  chatActiveId,
  chatConversations,
  chatHistoryOpen,
  chatLiveActivity,
  chatLiveText,
  chatMessages,
  chatProjectId,
  chatReadOnly,
  chatSending,
  chatTarget,
  chatTitleEditing,
  clarifyDialog,
  currencyEditCode,
  currencyPickerFilter,
  currencyPickerOpen,
  decisions,
  decisionsDemandId,
  decisionSummary,
  deleteConfirmOpen,
  demandListLoadedKey,
  demands,
  demandSavings,
  demandsLoadingMore,
  demandsNextCursor,
  demandStatusCounts,
  demandStatusFilter,
  descEditing,
  documentAnnotations,
  documentPreview,
  documentSelectionAnchor,
  editingNoteId,
  editorSegment,
  exportState,
  type FormRow,
  type IntakeDoc,
  libraryDocs,
  libraryFactDialogId,
  libraryFacts,
  libraryProjects,
  libraryRevisions,
  libraryScope,
  librarySelectionAnchor,
  lineDecisionDialog,
  lineEditorDialog,
  lineEvidenceDialog,
  lineEvidenceMessage,
  lineEvidenceSubmitting,
  lineSelectionAnchor,
  matrixFocus,
  matrixSelection,
  me,
  multifileUploadProgress,
  neededByEditing,
  notice,
  orderDialog,
  orgs,
  orgSavings,
  questionsDialogOpen,
  type QuestionsDraft,
  questionsDraft,
  quoteDialog,
  quoteMatrix,
  quoteMatrixLoadState,
  receiptDialog,
  reopenUpdateOpen,
  savingsPeriod,
  savingsSite,
  selectedAnalysisId,
  selectedDocumentIds,
  selectedLibraryDocIds,
  selectedLineIds,
  settings,
  shellSearch,
  shellSearchTokens,
  sourceEvidence,
  sourceRowPicker,
  status,
  titleEditing,
  vendorDetail,
  vendorDetailSection,
  vendorsOverview,
  view,
  workspace,
} from './state.js';
import { createVisibilityPolling, documentVisibility } from './visibility-polling.js';

/** After answering/skipping, close the questions dialog once none remain open. */
function closeQuestionsDialogIfDone(): void {
  const remaining = ((workspace.value?.forms ?? []) as FormRow[]).filter((f) => f.status === 'open');
  if (remaining.length === 0) questionsDialogOpen.value = false;
}

let chatStream: DemandEventStream | undefined; // the active conversation's SSE subscription
let chatStreamId = ''; // the conversation chatStream belongs to (HS2-ZVVES3)
// Bumped on every conversation load and chat reset, so only the newest request is
// applied and a list load does not override a pick made while it ran (HS2-YQJCJT).
let conversationLoadSeq = 0;
let chatResetSeq = 0; // bumped when the chat target changes
// The conversation the newest load is fetching, until it lands. A list load or a
// chat.done that starts while a pick is in flight leaves the selection to that
// pick instead of reopening the previous chat (HS2-S8QT8S, HS2-8T03YS).
let pendingConversationId: string | null = null;
// Project lists load from several places (Chat, the Knowledge Base, project settings) and can
// answer out of order. Only the newest request's list applies, so a slow, stale list never
// replaces a newer one (HS2-9AW3T9).
let projectListRequested = 0;
let projectListApplied = 0;
const listProjectsInOrder = async (
  orgId: string,
): Promise<{ apply: () => boolean; res: Awaited<ReturnType<typeof api.listProjects>> }> => {
  const generation = ++projectListRequested;
  const res = await api.listProjects(orgId);
  return {
    res,
    apply: () => {
      if (!res.ok || res.data === null || generation < projectListApplied) return false;
      projectListApplied = generation;
      libraryProjects.value = res.data.projects;
      return true;
    },
  };
};
const analysisRunGuard = createDemandTaskGuard(currentDemandId);
let demandOpenGeneration = 0;
/** The demand an in-flight authoritative open is loading; `workspace` still shows the previous one. */
let pendingOpenId: string | undefined;

/** Cancel an in-flight demand open when browser history selects another surface. */
export function invalidatePendingDemandOpen(): void {
  demandOpenGeneration++;
  pendingOpenId = undefined;
}

/** Start an authoritative demand open: newer than every earlier open or refresh. */
function beginDemandOpen(id: string): number {
  pendingOpenId = id;
  return ++demandOpenGeneration;
}

/** Settle an open; only the newest open clears the pending target. */
function endDemandOpen(generation: number): void {
  if (generation === demandOpenGeneration) pendingOpenId = undefined;
}

/** The demand the user is on, or is being taken to by an open still in flight. */
function shownDemandId(): string | undefined {
  return pendingOpenId ?? currentDemandId();
}

/**
 * Reload a demand after a change to it (an upload, an edit, a decision). The change was made on
 * the demand then on screen; when the user has since opened another one, reloading it would pull
 * them back, so this does nothing (HS2-BHWPY6).
 */
export async function reloadDemand(id: string): Promise<void> {
  if (shownDemandId() === id) await loadIntake(id);
}

/** `reloadDemand` through the full open (dialogs, panes, analysis), for status changes. */
export async function reopenDemandIfShown(id: string): Promise<void> {
  if (shownDemandId() === id) await openDemand(id);
}

/** Demand-list loads started so far, the newest one that succeeded, and the one in flight (HS2-46ZY3F). */
let demandLoadGeneration = 0;
let demandLoadSucceeded = 0;
/** The newest load whose response is on screen; an older response that lands later is dropped. */
let demandLoadApplied = 0;
let demandLoadInFlight: { generation: number; key: string; promise: Promise<boolean> } | undefined;
/** A list the shell bootstrap supplied (HS2-TMF3KZ); the first startup load joins it instead of fetching. */
let demandSeed: { generation: number; key: string } | undefined;

const DEMAND_LIST_VIEWS = new Set(['demands', 'workspace', 'intake']);

/** The list filter the current view asks the server for (HS2-X9K02S). */
function currentDemandListFilter(): string {
  return demandListFilterQuery({
    status: demandStatusFilter.value,
    search: shellSearch.value,
    tokens: shellSearchTokens.value,
    // The views whose sidebar is the demand list (`primaryView` 'demands').
    searchActive: isRequester() || DEMAND_LIST_VIEWS.has(view.value),
  });
}

/** The key of a list load: org plus filter, never the page size or cursor. */
function demandListKey(orgId: string, filter: string): string {
  return `${orgId}?${filter}`;
}

/** Apply a list response (a whole window from the top) as the visible list. */
function applyDemandWindow(key: string, page: DemandListResponse): void {
  // Long-poll handshakes reconcile authoritatively, but an identical response
  // must not replace the signal: a no-op shell rerender closes transient native
  // controls such as an open account dropdown.
  if (JSON.stringify(demands.value) !== JSON.stringify(page.demands)) demands.value = page.demands;
  if (demandsNextCursor.value !== page.nextCursor) demandsNextCursor.value = page.nextCursor;
  if (JSON.stringify(demandStatusCounts.value) !== JSON.stringify(page.statusCounts))
    demandStatusCounts.value = page.statusCounts;
  if (demandListLoadedKey.value !== key) demandListLoadedKey.value = key;
}

/**
 * Load the demand list's first page — or, when reloading the same filter, the
 * whole window already shown (up to {@link DEMAND_RELOAD_MAX} rows), so a change
 * feed refresh never drops rows the user paged in (HS2-X9K02S). Callers that just
 * changed a demand always fetch afresh; startup callers pass `shareInFlight` to
 * join an identical load already under way instead of starting another (HS2-46ZY3F).
 */
export function loadDemands(options: { shareInFlight?: boolean } = {}): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return Promise.resolve(false);
  const filter = currentDemandListFilter();
  const key = demandListKey(orgId, filter);
  if (options.shareInFlight === true && demandSeed?.key === key && demandSeed.generation === demandLoadGeneration) {
    demandSeed = undefined; // one startup caller joins the seed; later navigation fetches again
    return Promise.resolve(true);
  }
  if (options.shareInFlight === true && demandLoadInFlight?.key === key) return demandLoadInFlight.promise;
  const window =
    demandListLoadedKey.value === key
      ? Math.min(Math.max(DEMAND_PAGE_SIZE, demands.value.length), DEMAND_RELOAD_MAX)
      : DEMAND_PAGE_SIZE;
  const generation = ++demandLoadGeneration;
  const promise = (async (): Promise<boolean> => {
    const res = await api.listDemands(orgId, demandListRequestQuery(filter, window));
    if (!res.ok || res.data === null || me.value?.organizationId !== orgId) return false;
    if (generation < demandLoadApplied) return true; // a newer load already answered
    demandLoadApplied = generation;
    applyDemandWindow(key, res.data);
    demandLoadSucceeded = Math.max(demandLoadSucceeded, generation);
    return true;
  })().finally(() => {
    if (demandLoadInFlight?.generation === generation) demandLoadInFlight = undefined;
  });
  demandLoadInFlight = { generation, key, promise };
  return promise;
}

/**
 * Append the next page of the shown list (the sidebar's "Load more", HS2-X9K02S).
 * The page is dropped when a reload or a filter change landed meanwhile — that
 * response already replaced the window this cursor continued.
 */
export async function loadMoreDemands(): Promise<boolean> {
  const orgId = me.value?.organizationId;
  const cursor = demandsNextCursor.value;
  if (orgId === null || orgId === undefined || cursor === null || demandsLoadingMore.value) return false;
  const filter = currentDemandListFilter();
  const key = demandListKey(orgId, filter);
  if (demandListLoadedKey.value !== key) return false;
  const applied = demandLoadApplied;
  demandsLoadingMore.value = true;
  try {
    const res = await api.listDemands(orgId, demandListRequestQuery(filter, DEMAND_PAGE_SIZE, cursor));
    if (!res.ok || res.data === null) return false;
    const current = demandListKey(orgId, currentDemandListFilter());
    if (
      demandLoadApplied !== applied ||
      current !== key ||
      demandListLoadedKey.value !== key ||
      demandsNextCursor.value !== cursor
    )
      return false;
    const shown = new Set(demands.value.map((demand) => demand.id));
    demands.value = [...demands.value, ...res.data.demands.filter((demand) => !shown.has(demand.id))];
    demandsNextCursor.value = res.data.nextCursor;
    demandStatusCounts.value = res.data.statusCounts;
    return true;
  } finally {
    demandsLoadingMore.value = false;
  }
}

let demandSearchTimer: ReturnType<typeof setTimeout> | undefined;
/** How long the shell search waits after the last keystroke before asking the server. */
export const DEMAND_SEARCH_DEBOUNCE_MS = 250;

/**
 * Re-query the demand list when the shell search or its tokens change while the
 * demand list is the visible sidebar (HS2-X9K02S). The client filter narrows the
 * loaded rows at once; the debounced server load then finds the rest.
 */
export function watchDemandSearch(): () => void {
  let previous: string | undefined;
  return effect(() => {
    const filter = currentDemandListFilter();
    const first = previous === undefined;
    const changed = previous !== filter;
    previous = filter;
    if (first || !changed || status.value !== 'ready') return;
    clearTimeout(demandSearchTimer);
    demandSearchTimer = setTimeout(() => {
      demandSearchTimer = undefined;
      void loadDemands();
    }, DEMAND_SEARCH_DEBOUNCE_MS);
  });
}

/**
 * Apply the first page the shell bootstrap embedded (HS2-TMF3KZ) as a completed
 * load of the unfiltered list, so the startup history restore joins it rather than
 * fetching the same list again. Any later load supersedes it as usual.
 */
export function seedDemands(orgId: string, page: DemandListResponse): void {
  const generation = ++demandLoadGeneration;
  demandLoadApplied = generation;
  demandLoadSucceeded = Math.max(demandLoadSucceeded, generation);
  const filter = currentDemandListFilter();
  applyDemandWindow(demandListKey(orgId, ''), page);
  demandSeed = filter === '' ? { generation, key: demandListKey(orgId, '') } : undefined;
}

/** Whether a list load that started after `generation` has succeeded, waiting for one still in flight. */
async function demandsLoadedSince(generation: number): Promise<boolean> {
  if (demandLoadSucceeded > generation) return true;
  const inFlight = demandLoadInFlight;
  if (inFlight === undefined || inFlight.generation <= generation) return false;
  return (await inFlight.promise) && demandLoadSucceeded > generation;
}

export async function openDemand(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  if (multifileBatch?.demandId !== id) cancelMultifileBatch();
  const sameDemand = currentDemandId() === id;
  const openGeneration = beginDemandOpen(id);
  // A submit can refresh this demand while Price Check awaits its prior-version
  // lookup. Keep that task alive; only a switch to another demand cancels it.
  if (!sameDemand) {
    analysisRunGuard.invalidate();
    analysisRunning.value = false;
    analysisProgress.value = null;
  }
  const startView = view.value;
  const res = await api.getDemand(orgId, id);
  if (openGeneration !== demandOpenGeneration) return;
  endDemandOpen(openGeneration);
  // The viewer moved to another primary view (Chat, Stats, Settings, …) while this loaded:
  // a late open or refresh must not pull them back or reset their chat (HS2-9AW3T9).
  if (view.value !== startView && !DEMAND_LIST_VIEWS.has(view.value)) return;
  if (res.ok) {
    // Re-opening the demand already on screen (after a submit, say) is a refresh. While the
    // reviewer has a quotation, line or decision dialog open it must not close that dialog, nor blank the
    // matrix the dialog renders from; otherwise a refresh landing late wipes the dialog.
    const keepDialogs =
      currentDemandId() === id &&
      (quoteDialog.value !== null ||
        lineEditorDialog.value !== null ||
        lineEvidenceDialog.value !== null ||
        lineDecisionDialog.value !== null ||
        clarifyDialog.value !== null ||
        orderDialog.value !== null ||
        receiptDialog.value !== null);
    workspace.value = res.data;
    sourceEvidence.value = null;
    if (me.value?.roles.some((role) => role === 'reviewer' || role === 'admin') === true) {
      void reloadSourceEvidence(id);
      void loadLibraryProjects();
    }
    if (!canOpenDemandSegment(editorSegment.value)) editorSegment.value = 'lines';
    // Keep the current section when switching between existing demands so a
    // reviewer can compare the same surface across records (HS2-0ADV47).
    editingNoteId.value = null;
    titleEditing.value = false;
    descEditing.value = false;
    neededByEditing.value = false;
    if (!keepDialogs) {
      quoteMatrix.value = null;
      quoteMatrixLoadState.value = { status: 'idle' };
      quoteDialog.value = null;
      lineEditorDialog.value = null;
      clarifyDialog.value = null;
      orderDialog.value = null;
      receiptDialog.value = null;
      reopenUpdateOpen.value = false;
      lineEvidenceDialog.value = null;
      lineEvidenceMessage.value = '';
      lineEvidenceSubmitting.value = false;
    }
    lineSelectionAnchor.value = '';
    selectedLineIds.value = [];
    documentSelectionAnchor.value = '';
    selectedDocumentIds.value = [];
    matrixSelection.value = EMPTY_MATRIX_SELECTION;
    matrixFocus.value = null;
    documentPreview.value = null;
    assumptionsDialog.value = null;
    analysisReport.value = null;
    analysisList.value = [];
    selectedAnalysisId.value = '';
    if (!sameDemand) {
      analysisRunning.value = false;
      analysisProgress.value = null;
    }
    decisions.value = [];
    decisionsDemandId.value = '';
    decisionSummary.value = null;
    demandSavings.value = null;
    resetChat({ scope: 'demand', demandId: id });
    view.value = 'workspace';
    // Commercial surfaces are unavailable to requesters. Avoid issuing requests
    // their role cannot authorize; their redacted workspace already includes the
    // decision statuses rendered by the shared line-item view.
    if (!isRequester()) {
      // The unified editor shows Analysis + Decisions inline, so load them (and
      // the quote matrix) up front rather than on a tab switch (HS2-DHQFF6).
      void loadAnalyses(id);
      void loadDecisions();
      void loadQuoteMatrix(id, { fresh: true });
    }
    await loadChat();
  }
}

/**
 * Point the assistant back at the open demand's conversation when the viewer returns
 * to its workspace from Chat, Stats, Settings or the Knowledge Base, which each retarget
 * the shared chat state. Without this the demand rail shows the other view's chat
 * until something reloads it (HS2-69VJ88). A chat still on this demand is kept and
 * only reconnected, since leaving the view closed its stream.
 */
export async function restoreDemandChat(): Promise<void> {
  const demandId = currentDemandId();
  if (demandId === undefined) return;
  const target = chatTarget.value;
  if (target?.scope !== 'demand' || target.demandId !== demandId) resetChat({ scope: 'demand', demandId });
  await loadChat();
}

/** Point the chat panel at a target (demand / library / project) and clear state. */
export function resetChat(target: ChatTarget | null): void {
  conversationLoadSeq++;
  chatResetSeq++;
  pendingConversationId = null;
  closeChatStream();
  chatTarget.value = target;
  chatConversations.value = [];
  chatActiveId.value = '';
  chatMessages.value = [];
  chatReadOnly.value = false;
  chatHistoryOpen.value = false;
  chatLiveActivity.value = '';
  chatLiveText.value = '';
}

/** Tear down the conversation SSE subscription when leaving a chat surface. */
export function closeChatStream(): void {
  chatStream?.close();
  chatStream = undefined;
  chatStreamId = '';
}

/** Handle a chat SSE event: drive the live indicator and refresh on completion. */
export function onChatEvent(type: string, data: unknown): void {
  const d = (data ?? {}) as { conversationId?: string; label?: string; detail?: string; text?: string };
  switch (type) {
    case 'chat.start':
      chatLiveActivity.value = 'Thinking…';
      chatLiveText.value = '';
      break;
    case 'chat.tool':
      chatLiveActivity.value =
        d.label === undefined
          ? 'Working…'
          : d.detail !== undefined && d.detail !== ''
            ? `${d.label} · ${d.detail}`
            : d.label;
      break;
    case 'chat.delta':
      // Accumulate incremental assistant text into a live bubble (HS2-J7096Q).
      if (typeof d.text === 'string') chatLiveText.value += d.text;
      break;
    case 'chat.done':
      chatSending.value = false;
      chatLiveActivity.value = '';
      chatLiveText.value = '';
      // Reload the finished chat only while it is the one shown or being opened; a pick of
      // another chat that is still loading keeps the selection (HS2-8T03YS).
      if (d.conversationId === (pendingConversationId ?? chatActiveId.value)) void loadConversation(d.conversationId);
      if (view.value === 'conversations') void loadProjectChats();
      else void loadChat();
      if (chatTarget.value?.scope === 'demand') void refreshWorkspace();
      else void refreshLibrary(); // a chat may have proposed a fact — show it in the review list
      break;
  }
}

export async function loadChat(): Promise<void> {
  const orgId = me.value?.organizationId;
  const target = chatTarget.value;
  if (orgId === null || orgId === undefined || target === null) return;
  const seq = conversationLoadSeq;
  const reset = chatResetSeq;
  const res = await api.listConversations(orgId, target);
  if (!res.ok || res.data === null || reset !== chatResetSeq) return; // the target changed
  chatConversations.value = res.data.conversations;
  // A conversation opened while the list loaded, or still opening, owns the selection.
  if (seq !== conversationLoadSeq || pendingConversationId !== null) return;
  // Keep the active conversation if it still exists, else open the most recent.
  const stillActive = chatActiveId.value !== '' && res.data.conversations.some((c) => c.id === chatActiveId.value);
  const next = stillActive ? chatActiveId.value : (res.data.conversations[0]?.id ?? '');
  if (next !== '') await loadConversation(next);
  else {
    chatActiveId.value = '';
    chatMessages.value = [];
    closeChatStream();
  }
}

export async function loadConversation(cid: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const seq = ++conversationLoadSeq;
  pendingConversationId = cid;
  let res: Awaited<ReturnType<typeof api.getConversation>>;
  try {
    res = await api.getConversation(orgId, cid);
  } finally {
    // Only the newest load clears it, whether it answered or failed.
    if (seq === conversationLoadSeq) pendingConversationId = null;
  }
  if (seq !== conversationLoadSeq) return; // a newer load or reset superseded this one
  if (res.ok && res.data !== null) {
    const conversation = res.data.conversation;
    if (conversation.scope === 'project' && conversation.projectId !== null) {
      chatTarget.value = { scope: 'project', projectId: conversation.projectId };
      chatProjectId.value = conversation.projectId;
    } else if (conversation.scope === 'organization') {
      chatTarget.value = { scope: 'organization' };
    }
    chatActiveId.value = cid;
    if (conversation.scope === 'project' || conversation.scope === 'organization') rememberChat(cid);
    chatMessages.value = res.data.messages;
    chatReadOnly.value = res.data.readOnly;
    // Stream this conversation's live tool-activity and completion.
    closeChatStream();
    chatStreamId = cid;
    chatStream = connectConversationEvents(orgId, cid, (type, data) => {
      // A reply that finished before this (re)connection was subscribed published
      // its chat.done to nobody; check for it once the stream is live (HS2-ZVVES3).
      if (type === 'ready') {
        if (chatSending.value && chatActiveId.value === cid) void applyReplyIfDone(orgId, cid);
        return;
      }
      onChatEvent(type, data);
    });
  }
}

/** Load every shared project chat for the Chat view; demand conversations are
 * intentionally excluded by the server-side scope query. */
export async function loadProjectChats(): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const seq = conversationLoadSeq;
  const [projects, conversations, organizationConversations] = await Promise.all([
    listProjectsInOrder(orgId),
    api.listProjectConversations(orgId),
    api.listOrganizationConversations(orgId),
  ]);
  if (projects.apply()) {
    if (!libraryProjects.value.some((project) => project.id === chatProjectId.value))
      chatProjectId.value = libraryProjects.value[0]?.id ?? '';
  }
  if (!conversations.ok || conversations.data === null) return;
  const allConversations = [
    ...conversations.data.conversations,
    ...(organizationConversations.data?.conversations ?? []),
  ];
  chatConversations.value = allConversations;
  // A conversation opened while the list loaded, or still opening, owns the selection.
  if (seq !== conversationLoadSeq || pendingConversationId !== null) return;
  // Prefer the open chat, then the one this member last viewed (HS2-7CCP40), then the first.
  const remembered = rememberedChat();
  const active = allConversations.find((conversation) => conversation.id === chatActiveId.value);
  const last = allConversations.find((conversation) => conversation.id === remembered);
  if (remembered !== undefined && last === undefined) forgetChat();
  const next = active ?? last ?? allConversations[0];
  if (next !== undefined) await openProjectChat(next.id);
  else {
    chatActiveId.value = '';
    chatMessages.value = [];
    chatTarget.value = chatProjectId.value === '' ? null : { scope: 'project', projectId: chatProjectId.value };
    closeChatStream();
  }
}

/** The pref key for the Chat view's last-viewed conversation. A member id is one
 *  user in one organization, so a remembered chat never crosses tenants or the
 *  people sharing a browser (HS2-7CCP40). */
function lastChatKey(): string | undefined {
  const memberId = me.value?.memberId;
  return memberId === null || memberId === undefined || memberId === '' ? undefined : `lastChat.${memberId}`;
}

function rememberedChat(): string | undefined {
  const key = lastChatKey();
  return key === undefined ? undefined : loadString(key);
}

function rememberChat(cid: string): void {
  const key = lastChatKey();
  if (key !== undefined) saveString(key, cid);
}

function forgetChat(): void {
  const key = lastChatKey();
  if (key !== undefined) removePref(key);
}

export async function openProjectChat(cid: string): Promise<void> {
  chatTitleEditing.value = false;
  await loadConversation(cid);
}

export async function createProjectChat(projectId: string): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined || projectId === '') {
    notice.value = 'Create or select a project before starting a chat.';
    return false;
  }
  // Default the title to the project name + today's date (HS2-1E0YTJ); the user
  // can rename it inline afterwards.
  const projectName = libraryProjects.value.find((p) => p.id === projectId)?.name ?? 'Project';
  const target: ChatTarget = { scope: 'project', projectId };
  chatProjectId.value = projectId;
  const res = await api.createConversation(orgId, target, `${projectName} — ${new Date().toLocaleDateString()}`);
  if (!res.ok || res.data === null) {
    notice.value = res.error ?? 'Could not start the project chat.';
    return false;
  }
  chatTarget.value = target;
  chatActiveId.value = res.data.id;
  await loadProjectChats();
  chatTitleEditing.value = true;
  return true;
}

export async function createOrganizationChat(): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return false;
  const target: ChatTarget = { scope: 'organization' };
  const res = await api.createConversation(orgId, target, `Organization — ${new Date().toLocaleDateString()}`);
  if (!res.ok || res.data === null) {
    notice.value = res.error ?? 'Could not start the organization chat.';
    return false;
  }
  chatTarget.value = target;
  chatActiveId.value = res.data.id;
  await loadProjectChats();
  chatTitleEditing.value = true;
  return true;
}

export async function saveChatTitle(title: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const cid = chatActiveId.value;
  const trimmed = title.trim();
  if (orgId === null || orgId === undefined || cid === '' || trimmed === '') return;
  const res = await api.updateConversationTitle(orgId, cid, trimmed);
  if (!res.ok) {
    notice.value = res.error ?? 'Could not rename the chat.';
    return;
  }
  chatConversations.value = chatConversations.value.map((conversation) =>
    conversation.id === cid ? { ...conversation, title: trimmed, updatedAt: new Date().toISOString() } : conversation,
  );
}

/** Close (archive) or reopen a project chat, then reload the sidebar so the row
 *  moves between the Open Chats and Closed Chats groups (HS2-AEX5AM). */
export async function closeProjectChat(cid: string, closed: boolean): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined || cid === '') return;
  const res = await api.setConversationClosed(orgId, cid, closed);
  if (!res.ok) {
    notice.value = res.error ?? (closed ? 'Could not close the chat.' : 'Could not reopen the chat.');
    return;
  }
  await loadProjectChats();
}

/**
 * Re-fetch the workspace payload without disturbing chat or tab state. Resolves false when the
 * response was discarded because the user was interacting, so a reconcile can retry it later.
 */
export async function refreshWorkspace(): Promise<boolean> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return true;
  // A background refresh (poll / long-poll reconcile) must not clobber a demand
  // the user opened while this request was in flight. Capture the open generation
  // and re-check the current demand before assigning, so a newer open always wins
  // (HS2-01BKNA: New demand focused the previously-open demand's title).
  const generation = demandOpenGeneration;
  // Replacing the workspace rerenders the editor. If the user interacts after
  // this read starts, that rerender would dismiss an open dropdown/dialog or
  // erase a transient line-reference highlight. Treat that response as stale;
  // the next poll will reconcile once the interaction has settled.
  const interaction = interactionGeneration();
  const res = await api.getDemand(orgId, demandId);
  // A newer open loads its own demand, and a failed read is not retried here.
  if (!res.ok || generation !== demandOpenGeneration || currentDemandId() !== demandId) return true;
  if (interaction !== interactionGeneration() || transientInteractionOpen()) return false;
  workspace.value = res.data;
  if (res.data !== null) reconcileAnalyses(demandId, res.data.analyses);
  return true;
}

/** Reload the pricing checks when a workspace read shows their statuses changed (FR-5.8). A run in
 *  progress reloads them itself when it finishes. */
function reconcileAnalyses(demandId: string, rows: readonly { id: string; status?: unknown }[]): void {
  if (!analysisRunning.value && analysesNeedReload(analysisList.value, rows)) void loadAnalyses(demandId);
}

export async function loadIntake(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  // Opening intake is authoritative: bump the open generation so any in-flight
  // background refresh of a different demand cannot overwrite this one (HS2-01BKNA).
  const generation = beginDemandOpen(id);
  const res = await api.getDemand(orgId, id);
  endDemandOpen(generation);
  if (res.ok && generation === demandOpenGeneration) {
    workspace.value = res.data;
    if (res.data !== null) reconcileAnalyses(id, res.data.analyses);
    reconcileMultifileBatch(id);
  }
  if (generation === demandOpenGeneration) {
    sourceEvidence.value = null;
    sourceRowPicker.value = null;
    if (me.value?.roles.some((role) => role === 'reviewer' || role === 'admin') === true) void reloadSourceEvidence(id);
  }
  syncIntakePolling();
}

/**
 * The intake poll's reload while documents extract. Unlike `loadIntake` it is a background read: it
 * never bumps the open generation, so a demand the user opens meanwhile (a duplicate, a sidebar row)
 * wins even though `currentDemandId()` still names the old one until that open lands (HS2-BHWPY6).
 */
async function refreshIntake(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const generation = demandOpenGeneration;
  const res = await api.getDemand(orgId, id);
  if (generation !== demandOpenGeneration || currentDemandId() !== id) return;
  if (res.ok) workspace.value = res.data;
  if (res.ok) reconcileMultifileBatch(id);
  // Source rows appear as extraction finishes, so staff evidence follows each tick.
  if (me.value?.roles.some((role) => role === 'reviewer' || role === 'admin') === true) void reloadSourceEvidence(id);
  syncIntakePolling();
}

export async function reloadSourceEvidence(demandId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const result = await api.sourceEvidence(orgId, demandId);
  if (currentDemandId() === demandId) sourceEvidence.value = result.ok ? result.data : null;
}

let sourceRowSearchRequest = 0;

/** The line the source-row picker is open for, read fresh (it may close mid-request). */
function openSourceRowPickerLine(): string | undefined {
  return sourceRowPicker.value?.lineItemId;
}

/** Open the source-row picker for a line, or re-run its search for a new query (HS2-4TER5Q). */
export async function searchSourceRowsForPicker(lineItemId: string, query: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  const previous = sourceRowPicker.value;
  sourceRowPicker.value = {
    lineItemId,
    query,
    loading: true,
    result: previous?.lineItemId === lineItemId ? previous.result : null,
    error: null,
  };
  const request = ++sourceRowSearchRequest;
  const res = await api.searchSourceRows(orgId, demandId, lineItemId, query);
  // A newer keystroke, a closed picker, or another demand supersedes this answer.
  if (request !== sourceRowSearchRequest || openSourceRowPickerLine() !== lineItemId) return;
  if (currentDemandId() !== demandId) return;
  sourceRowPicker.value = {
    lineItemId,
    query,
    loading: false,
    result: res.ok ? res.data : null,
    error: res.ok ? null : (res.error ?? 'Could not search source rows.'),
  };
}

// While any uploaded doc is still queued/reading, poll the demand so extraction
// status and detected line items appear without a manual refresh. Active polling
// also keeps the (scale-to-zero) instance warm so the worker processes the jobs.
// The 3 s tick pauses while the tab is hidden and reconciles once on return to
// visible, so a hidden tab mid-extraction sends nothing (HS2-WF0TN5).
let intakePolling = false;
const intakePoll = createVisibilityPolling({
  visibility: documentVisibility(),
  reconcile: () => {
    if (view.value !== 'intake' && view.value !== 'workspace') {
      stopIntakePolling();
      return;
    }
    const id = currentDemandId();
    if (id !== undefined) void refreshIntake(id);
  },
  reconcileMs: 3000,
});
export function stopIntakePolling(): void {
  intakePolling = false;
  intakePoll.stopReconcile();
}
export function syncIntakePolling(): void {
  const ws = workspace.value;
  const docs =
    ws !== null ? Object.values((ws.filesByKind ?? {}) as Record<string, { extractionStatus?: string }[]>).flat() : [];
  const inProgress = docs.some((d) => d.extractionStatus === 'pending' || d.extractionStatus === 'reading');
  const active = (view.value === 'intake' || view.value === 'workspace') && inProgress;
  if (active && !intakePolling) {
    intakePolling = true;
    intakePoll.startReconcile();
  } else if (!active) {
    stopIntakePolling();
  }
}

// The assistant creates question forms asynchronously in the worker, so the client
// polls (HS2-BXQYPY) to surface them promptly without a manual refresh: the demand
// list (for the sidebar's pending-questions markers) on every tick, and the open
// demand workspace (for its questions banner) when a demand is open. The workspace
// refresh is skipped while the user is mid-edit or a dialog is open so nothing they
// are typing gets clobbered, and while intake polling is already reloading it.
// Runs only while signed in. Worker jobs (question forms, extraction) publish demand
// changes, so the 20 s reconcile is only a fallback that runs while the change long poll
// is failing or reconnecting (HS2-HF0QT0). Both pause while the tab is hidden and catch
// up once on return (HS2-BZ9S6A).
const demandPolling = createVisibilityPolling({
  visibility: documentVisibility(),
  reconcile: () => reconcileDemandSurfaces(),
  reconcileMs: 20_000,
});

export function stopDemandChangePolling(): void {
  demandPolling.stopChanges();
  cancelDeferredWorkspaceRefresh();
}

/** Start the demand-change long poll; `cursor` resumes from the shell bootstrap's cursor (HS2-TMF3KZ). */
export function startDemandChangePolling(cursor?: number): void {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  demandPolling.startChanges((resumeCursor, health) => {
    // A handshake reconciles the list with the change cursor. When a list load that
    // started after the handshake was sent has succeeded (the startup load), the list
    // is already at least that fresh, so the handshake does not fetch it again
    // (HS2-46ZY3F). A reconnect, or a resume after the tab was hidden, has no such
    // load and still reconciles.
    let handshakeGeneration = 0;
    return startDemandChangePoll({
      ...(resumeCursor === undefined ? {} : { initialCursor: resumeCursor }),
      client: {
        pollDemandChanges: (since, signal) => {
          if (since === undefined) handshakeGeneration = demandLoadGeneration;
          return api.pollDemandChanges(orgId, since, signal);
        },
      },
      refresh: async (handshake) => {
        if (handshake && (await demandsLoadedSince(handshakeGeneration))) return;
        if (!(await reconcileDemandSurfaces())) throw new Error('demand list reconciliation failed');
      },
      onError: () => {
        health(false);
      },
      onHealthy: () => {
        health(true);
      },
    });
  }, cursor);
}

export function stopQuestionPolling(): void {
  demandPolling.stopReconcile();
}
export function startQuestionPolling(): void {
  demandPolling.startReconcile();
}

async function reconcileDemandSurfaces(): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined || !(await loadDemands())) return false;
  await reconcileOpenDemand();
  return true;
}

/**
 * Refresh the open demand for a reconcile. While the user is editing, has a dialog open, or
 * interacts during the read, the refresh is put off and retried shortly: with no periodic
 * reconcile while the change long poll is healthy (HS2-HF0QT0), nothing else would bring a
 * skipped change (a new question form) in until the next demand change.
 */
async function reconcileOpenDemand(): Promise<void> {
  const id = currentDemandId();
  const onDemand = (view.value === 'workspace' || view.value === 'intake') && id !== undefined;
  if (!onDemand || intakePolling) return; // the intake poll is already reloading it
  const editing = titleEditing.value || descEditing.value || editingNoteId.value !== null || neededByEditing.value;
  const dialogOpen = quoteDialog.value !== null || lineDecisionDialog.value !== null;
  if (editing || dialogOpen || !(await refreshWorkspace())) deferWorkspaceRefresh();
  else cancelDeferredWorkspaceRefresh();
}

const DEFERRED_WORKSPACE_REFRESH_MS = 3000;
let deferredWorkspaceRefresh: ReturnType<typeof setTimeout> | undefined;
function deferWorkspaceRefresh(): void {
  if (deferredWorkspaceRefresh !== undefined) return;
  deferredWorkspaceRefresh = setTimeout(() => {
    deferredWorkspaceRefresh = undefined;
    // A hidden tab sends nothing; its return to visible reconciles through the handshake.
    if (documentVisibility().isHidden() || me.value === null) return;
    void reconcileOpenDemand();
  }, DEFERRED_WORKSPACE_REFRESH_MS);
}
function cancelDeferredWorkspaceRefresh(): void {
  if (deferredWorkspaceRefresh === undefined) return;
  clearTimeout(deferredWorkspaceRefresh);
  deferredWorkspaceRefresh = undefined;
}

const requestQuoteMatrix = createQuoteMatrixLoader({
  request: (orgId, demandId) => api.getQuoteMatrix(orgId, demandId),
  currentKey: () => {
    const orgId = me.value?.organizationId;
    const demandId = currentDemandId();
    return orgId === null || orgId === undefined || demandId === undefined ? undefined : `${orgId}:${demandId}`;
  },
  setMatrix: (matrix) => {
    quoteMatrix.value = matrix;
  },
  setState: (state) => {
    quoteMatrixLoadState.value = state;
  },
});

/** Load the quote matrix. Pass `fresh` after a change (saving a quotation, re-opening the demand) so the result
 *  cannot be an in-flight response that predates it; plain loads (opening a tab) share an in-flight request. */
export async function loadQuoteMatrix(explicitDemandId?: string, options?: QuoteMatrixLoadOptions): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = explicitDemandId ?? currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  await requestQuoteMatrix(orgId, demandId, options);
}

export async function loadAnalyses(explicitDemandId?: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = explicitDemandId ?? currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  const res = await api.listAnalyses(orgId, demandId);
  if (currentDemandId() !== demandId) return;
  if (res.ok && res.data !== null) {
    analysisList.value = res.data.analyses;
    const pick = analysisList.value.find((a) => a.id === selectedAnalysisId.value) ?? analysisList.value[0];
    if (pick !== undefined) {
      selectedAnalysisId.value = pick.id;
      await loadAnalysisReport(pick.id, demandId);
    } else {
      analysisReport.value = null;
    }
  }
}

export async function loadAnalysisReport(analysisId: string, explicitDemandId?: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = explicitDemandId ?? currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  const res = await api.getAnalysis(orgId, demandId, analysisId);
  if (currentDemandId() === demandId && selectedAnalysisId.value === analysisId && res.ok)
    analysisReport.value = res.data;
}

export async function loadDecisions(): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  const [dec, sum, sav] = await Promise.all([
    api.listDecisions(orgId, demandId),
    api.getDecisionSummary(orgId, demandId),
    api.getDemandSavings(orgId, demandId),
  ]);
  if (dec.ok && dec.data !== null && currentDemandId() === demandId) {
    decisions.value = dec.data.current;
    decisionsDemandId.value = demandId;
  }
  if (sum.ok) decisionSummary.value = sum.data;
  if (sav.ok) demandSavings.value = sav.data;
}

export async function loadOrgSavings(): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const days = savingsPeriodDays(savingsPeriod.value);
  const params = new URLSearchParams();
  if (days > 0) params.set('from', new Date(Date.now() - days * 86_400_000).toISOString());
  if (savingsSite.value !== '') params.set('site', savingsSite.value);
  const query = params.toString() === '' ? '' : `?${params.toString()}`;
  const res = await api.getOrgSavings(orgId, query);
  if (res.ok) orgSavings.value = res.data;
}

export async function loadVendors(): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const res = await api.vendorsOverview(orgId);
  if (res.ok) vendorsOverview.value = res.data;
}

export async function openVendor(vendorId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const res = await api.vendorHistory(orgId, vendorId);
  if (res.ok) {
    vendorDetailSection.value = 'details';
    vendorDetail.value = res.data;
  }
}

/**
 * Load the Knowledge Base for the current scope. Opening it (navigation) closes a
 * library image preview; a `refresh` after an in-place change keeps the preview
 * open unless its file has left the list, so the post-upload polling does not
 * close a preview the user just opened (HS2-Y7S8TM).
 */
export async function loadLibrary(options: { refresh?: boolean } = {}): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  if (options.refresh !== true && documentPreview.value?.source === 'library') documentPreview.value = null;
  // Opening a scope starts with nothing selected. Clear it now, not when the list arrives: a tile
  // the viewer selects (or right-clicks) while the load is in flight keeps its selection, so its
  // context menu stays enabled (HS2-WCAQZH).
  if (options.refresh !== true) {
    selectedLibraryDocIds.value = [];
    librarySelectionAnchor.value = '';
  }
  const scope = libraryScope.value === '' ? undefined : libraryScope.value;
  const [projs, docs, facts] = await Promise.all([
    listProjectsInOrder(orgId),
    api.listBackground(orgId, scope),
    api.listFacts(orgId, scope),
  ]);
  projs.apply();
  if (docs.ok && docs.data !== null) {
    libraryDocs.value = docs.data.documents;
    // Keep the selection that is still listed: a refresh's, or one made while this load ran.
    const listed = new Set(docs.data.documents.map((doc) => doc.id));
    selectedLibraryDocIds.value = selectedLibraryDocIds.value.filter((id) => listed.has(id));
    if (!selectedLibraryDocIds.value.includes(librarySelectionAnchor.value)) librarySelectionAnchor.value = '';
    const preview = currentDocumentPreview();
    if (preview?.source === 'library' && !docs.data.documents.some((doc) => doc.id === preview.documentId))
      documentPreview.value = null;
  }
  if (facts.ok && facts.data !== null) libraryFacts.value = facts.data.facts;
}

/** Re-read the Knowledge Base after an in-place change, keeping an open preview. */
function refreshLibrary(): Promise<void> {
  return loadLibrary({ refresh: true });
}

/** Point the chat panel at the current Background scope (library or a project). */
export async function syncLibraryChatTarget(): Promise<void> {
  const target: ChatTarget =
    libraryScope.value === '' ? { scope: 'library' } : { scope: 'project', projectId: libraryScope.value };
  resetChat(target);
  await loadChat();
}

export async function openIntake(id: string): Promise<void> {
  // A brand-new draft starts on Line items; section preservation applies only
  // when switching among existing demands (HS2-0ADV47).
  editorSegment.value = 'lines';
  await loadIntake(id);
  view.value = 'intake';
  syncIntakePolling();
}

/** Cancel / reopen a demand by id from the sidebar context menu (HS2-3BVPEF).
 *  Refreshes the list, and re-opens the demand if it is the one currently open so
 *  its editor reflects the new status. Cancel confirms first, like the footer action. */
export async function cancelDemandClient(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  if (!window.confirm('Cancel this demand?')) return;
  await fetch(`/api/orgs/${orgId}/demands/${id}/cancel`, { method: 'POST', credentials: 'same-origin' });
  await loadDemands();
  await reopenDemandIfShown(id);
}
export async function reopenDemandClient(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  await fetch(`/api/orgs/${orgId}/demands/${id}/reopen`, { method: 'POST', credentials: 'same-origin' });
  await loadDemands();
  await reopenDemandIfShown(id);
}

/** Duplicate a demand as a new draft and open it (HS2-Q2NPBT). */
export async function duplicateDemandClient(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const res = await fetch(`/api/orgs/${orgId}/demands/${id}/duplicate`, { method: 'POST', credentials: 'same-origin' });
  if (!res.ok) {
    notice.value = 'Could not duplicate this demand.';
    return;
  }
  const { id: copyId } = (await res.json()) as { id: string };
  await loadDemands();
  await openDemand(copyId);
}

/** Load the staff members a demand can be assigned to (FR-5.20, HS2-C63T9N); staff only. */
export async function loadAssignableMembers(): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined || !canAssignDemands()) {
    if (assignableMembers.value.length > 0) assignableMembers.value = [];
    return;
  }
  const res = await api.listAssignees(orgId);
  if (res.ok && res.data !== null && JSON.stringify(res.data.members) !== JSON.stringify(assignableMembers.value))
    assignableMembers.value = res.data.members;
}

/**
 * Set or clear a demand's assignee (FR-5.20, HS2-C63T9N). `expected` is the assignee the caller saw;
 * a concurrent change answers 409, which reloads the list and demand so the member sees the new one.
 */
export async function assignDemandClient(
  id: string,
  assigneeMemberId: string | null,
  expected: string | null,
): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const res = await api.assignDemand(orgId, id, { assigneeMemberId, expectedAssigneeMemberId: expected });
  if (!res.ok)
    notice.value =
      res.status === 409
        ? 'Someone else changed who this demand is assigned to. Showing the current assignee.'
        : (res.error ?? 'Could not change who this demand is assigned to.');
  // A member who just stopped being staff drops out of the choices.
  if (res.status === 400) void loadAssignableMembers();
  await Promise.all([loadDemands(), reloadDemand(id)]);
}

/** Withdraw the requester's submitted demand back to a draft (HS2-X22G63). */
export async function withdrawDemandClient(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const res = await fetch(`/api/orgs/${orgId}/demands/${id}/withdraw`, { method: 'POST', credentials: 'same-origin' });
  if (!res.ok) notice.value = 'This demand can no longer be withdrawn; the procurement team has started on it.';
  await loadDemands();
  await reopenDemandIfShown(id);
}

/** Reopen a decided or closed demand for changes, or lock it again (HS2-Y50H8E). */
export async function setDemandReopenedForChangesClient(id: string, reopen: boolean): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const route = reopen ? 'reopen-for-changes' : 'lock-changes';
  const res = await fetch(`/api/orgs/${orgId}/demands/${id}/${route}`, { method: 'POST', credentials: 'same-origin' });
  if (!res.ok) notice.value = reopen ? 'Could not reopen this demand for changes.' : 'Could not lock this demand.';
  await reopenDemandIfShown(id);
}

/** Drive the account button's background-image (HS2-26MSM4): the group has no
 *  style prop, so its picture URL rides a :root custom property. */
function setProfileAvatarVar(picture: string): void {
  document.documentElement.style.setProperty('--profile-avatar', picture !== '' ? `url("${picture}")` : 'none');
}

/**
 * Resolve the session and load what the signed-in shell needs first. A cold load
 * takes `me`, the organization list, the demand list and the change cursor from
 * the shell page's embedded bootstrap (HS2-TMF3KZ) — single use, so later calls
 * (sign-out, a new organization) and any page without a usable bootstrap fetch.
 */
export async function loadMe(): Promise<void> {
  const boot = takeShellBootstrap();
  const res = boot === null ? await api.me() : { ok: true, data: boot.me };
  if (!res.ok || res.data === null || !res.data.authenticated) {
    status.value = 'anon';
    me.value = null;
    setProfileAvatarVar('');
    stopQuestionPolling();
    stopDemandChangePolling();
    return;
  }
  me.value = res.data;
  setProfileAvatarVar(res.data.picture ?? '');
  if (res.data.organizationChoiceRequired === true && !res.data.suspended) {
    stopQuestionPolling();
    stopDemandChangePolling();
    notice.value = '';
    const available = await api.myOrgs().catch(() => null);
    orgs.value = available?.ok === true && available.data !== null ? available.data.organizations : [];
    if (available?.ok !== true) notice.value = available?.error ?? 'Could not load your organizations.';
    status.value = 'choosing-organization';
    return;
  }
  // A suspended member is signed in but can do nothing (server denies every
  // action); show a clear message rather than dropping them into silent 403s.
  const nextStatus = res.data.suspended ? 'suspended' : res.data.registered ? 'ready' : 'onboarding';
  const seeded =
    boot?.workspace !== undefined && boot.workspace.organizationId === res.data.organizationId
      ? boot.workspace
      : undefined;
  if (nextStatus === 'ready') {
    view.value = 'demands';
    if (seeded !== undefined) {
      // Seed before `ready` so the startup history restore joins the embedded list,
      // and resume the long poll from the cursor read before that list.
      seedDemands(seeded.organizationId, {
        demands: seeded.demands,
        nextCursor: seeded.demandsNextCursor,
        statusCounts: seeded.demandsStatusCounts,
      });
      orgs.value = seeded.organizations;
      startDemandChangePolling(seeded.demandsCursor);
    } else {
      // Handshake before the first list load so that load also satisfies it (HS2-46ZY3F).
      startDemandChangePolling();
    }
  }
  status.value = nextStatus;
  if (status.value === 'ready') {
    if (seeded === undefined) {
      // The org list and the demand list load together; history restore joins the same list load.
      const [o] = await Promise.all([api.myOrgs(), loadDemands({ shareInFlight: true })]);
      if (o.ok && o.data !== null) orgs.value = o.data.organizations;
    }
    startQuestionPolling();
    void loadAssignableMembers();
  } else {
    stopQuestionPolling();
    stopDemandChangePolling();
  }
}

let settingsLoadSerial = 0;

export async function loadSettings(): Promise<void> {
  // Personal preferences come from /api/me. Requesters cannot read the
  // organization settings/AI-usage endpoints and never render those surfaces.
  if (isRequester()) return;
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const serial = ++settingsLoadSerial;
  exportState.value = { status: 'idle' };
  deleteConfirmOpen.value = false;
  const res = await api.getSettings(orgId);
  // A newer load (opening Settings, then a page) owns the result and the AI usage reload. A superseded
  // one that finished late used to reload AI usage again, blanking a page that had just rendered (HS2-S27KNJ).
  if (serial !== settingsLoadSerial) return;
  if (res.ok) {
    settings.value = res.data;
    assumptionFxCodes.value = orderedCurrencyCodes(
      Object.keys(effectiveFxRates((res.data as { assumptions?: object } | null)?.assumptions)),
    );
  }
  await loadAiUsage();
}

let aiUsageLoadSerial = 0;
/** The organization whose usage `aiUsage` holds; a refresh for it keeps showing that data. */
let aiUsageOrgId: string | null = null;

export async function loadAiUsage(): Promise<void> {
  const serial = ++aiUsageLoadSerial;
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) {
    aiUsage.value = null;
    aiUsageOrgId = null;
    aiUsageLoadState.value = 'idle';
    return;
  }
  // Refreshing the same organization keeps its usage on screen until the new response lands; blanking
  // it re-rendered an already visible page as a spinner on every settings navigation (HS2-S27KNJ).
  if (aiUsageOrgId !== orgId) aiUsage.value = null;
  aiUsageLoadState.value = 'loading';
  const failed = (): void => {
    aiUsage.value = null;
    aiUsageOrgId = null;
    aiUsageLoadState.value = 'error';
  };
  try {
    const res = await api.getAiUsage(orgId);
    if (serial !== aiUsageLoadSerial || me.value?.organizationId !== orgId) return;
    if (res.ok && res.data !== null) {
      aiUsage.value = res.data;
      aiUsageOrgId = orgId;
      aiUsageLoadState.value = 'ready';
    } else failed();
  } catch {
    if (serial === aiUsageLoadSerial && me.value?.organizationId === orgId) failed();
  }
}

export async function saveProfile(root: HTMLElement): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return false;
  // A wa-select's shadow input holds the option label, not its value (HS2-ESARMH).
  const grouping = selectValue(root.querySelector('[name="p_grouping"]'));
  const body: Profile = {
    name: field(root, 'p_name'),
    country: field(root, 'p_country') || undefined,
    baseCurrency: field(root, 'p_currency') || undefined,
    // The p_grouping select yields a raw string; narrow it to the schema enum.
    numberGrouping: grouping === 'pakistani' || grouping === 'international' ? grouping : undefined,
  };
  const res = await api.updateProfile(orgId, body);
  if (res.ok) {
    notice.value = '';
    showToast('Profile saved.');
    await loadSettings();
    return true;
  }
  notice.value = res.error ?? 'Save failed.';
  return false;
}

export async function saveThresholds(root: HTMLElement): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return false;
  // The thresholds route replaces the whole verdictThresholds object and requires
  // all five percentages (thresholdsSchema is not partial), so guarantee numbers
  // here. The form is always pre-populated, so numField only falls back defensively.
  const body: Thresholds = {
    fairPct: numField(root, 't_fairPct') ?? 0,
    slightlyHighPct: numField(root, 't_slightlyHighPct') ?? 0,
    highPct: numField(root, 't_highPct') ?? 0,
    belowMarketPct: numField(root, 't_belowMarketPct') ?? 0,
    importPreferenceMarginPct: numField(root, 't_importPreferenceMarginPct') ?? 0,
  };
  const res = await api.updateThresholds(orgId, body);
  if (res.ok) {
    notice.value = '';
    showToast('Thresholds saved.');
    await loadSettings();
    return true;
  }
  notice.value = res.error ?? 'Save failed.';
  return false;
}

export function selectFxCurrency(code: string): void {
  if (!isCurrencyCode(code) || assumptionFxCodes.value.includes(code)) return;
  currencyPickerOpen.value = false;
  currencyPickerFilter.value = '';
  currencyEditCode.value = code;
}

export async function saveFxCurrency(root: HTMLElement, remove = false): Promise<void> {
  const code = currencyEditCode.value;
  const orgId = me.value?.organizationId;
  if (code === null || orgId === null || orgId === undefined) return;
  let rate: number | null = null;
  if (!remove) {
    rate = numField(root, 'currency-rate') ?? null;
    if (rate === null || rate <= 0) {
      notice.value = 'Enter an exchange rate greater than zero.';
      return;
    }
  }
  const fxRates = currencyRatesWithChange((settings.value?.assumptions ?? {}) as Record<string, unknown>, code, rate);
  const res = await api.updateAssumptions(orgId, { fxRates });
  if (!res.ok) {
    notice.value = res.error ?? 'Could not save currency.';
    return;
  }
  currencyEditCode.value = null;
  notice.value = '';
  showToast(remove ? 'Currency removed.' : 'Currency saved.');
  await loadSettings();
}

export async function saveAssumptions(root: HTMLElement): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return false;
  const res = await api.updateAssumptions(orgId, {
    insurancePct: numField(root, 'a_insurancePct'),
    customsDutyPct: numField(root, 'a_customsDutyPct'),
    salesTaxPct: numField(root, 'a_salesTaxPct'),
    financingCostPct: numField(root, 'a_financingCostPct'),
  });
  if (res.ok) {
    notice.value = '';
    showToast('Assumptions saved.');
    await loadSettings();
    return true;
  }
  notice.value = res.error ?? 'Save failed.';
  return false;
}

/** ~84 s of polling before an export is reported as failed. */
const EXPORT_POLL_LIMIT = 120;

/** Start a browser download of `url`; the server's attachment disposition keeps the page in place. */
function startDownload(url: string, filename = ''): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
}

/**
 * One step: request the export, wait for the worker, then download it automatically.
 * A failure is a toast rather than inline card text because the user may have
 * navigated away while the export was preparing.
 */
export async function exportOrgClient(): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined || exportState.value.status === 'running') return;
  exportState.value = { status: 'running' };
  const fail = (): void => {
    exportState.value = { status: 'idle' };
    showToast('Organization data export failed. Try again.', 'danger');
  };
  const res = await api.exportOrg(orgId);
  if (!res.ok || res.data === null) {
    fail();
    return;
  }
  const jobId = res.data.jobId;
  // The export runs in the worker; poll for the signed download link.
  for (let i = 0; i < EXPORT_POLL_LIMIT; i++) {
    await sleep(700);
    const poll = await api.pollExport(orgId, jobId);
    if (poll.ok && poll.data !== null) {
      if (poll.data.status === 'done' && poll.data.url !== undefined) {
        startDownload(poll.data.url);
        exportState.value = { status: 'idle' };
        return;
      }
      if (poll.data.status === 'failed') {
        fail();
        return;
      }
    }
  }
  fail();
}

/**
 * End the session and reload `/`, which renders the anonymous Sign In card. A full
 * reload, like an organization switch, leaves no view holding the old session's
 * state. A failed request keeps the viewer where they are with a notice rather
 * than reloading into a session that is still live (HS2-ZKA0HA).
 */
export async function signOut(): Promise<void> {
  closeChatStream();
  const res = await api.signout().catch(() => null);
  if (res?.ok !== true) {
    notice.value = res?.error ?? 'Could not sign out. Check your connection and try again.';
    return;
  }
  window.location.assign('/');
}

/**
 * Leave a member's read-only view and go back to the operator console with the
 * operator's own session restored (HS2-RHZ75A).
 */
export async function stopActing(): Promise<void> {
  closeChatStream();
  const res = await api.stopActing().catch(() => null);
  if (res?.ok !== true) {
    notice.value = res?.error ?? 'Could not stop viewing as this member. Check your connection and try again.';
    return;
  }
  window.location.assign('/admin');
}

export async function deleteOrgClient(root: HTMLElement): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const orgName = (settings.value?.profile as { name?: string } | undefined)?.name ?? '';
  const typed = elValue(root.querySelector('[name="delete-confirm"]')).trim();
  if (typed !== orgName || orgName === '') {
    notice.value = 'Type the exact organization name to confirm deletion.';
    return;
  }
  const res = await api.deleteOrg(orgId);
  if (res.ok) {
    deleteConfirmOpen.value = false;
    await signOut();
  } else notice.value = res.error ?? 'Could not delete the organization.';
}

export async function saveAiBudget(root: HTMLElement): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  // Blank clears a limit (null); a value sets it.
  const toLimit = (name: string): number | null => {
    const v = field(root, name);
    return v === '' ? null : Number(v);
  };
  const res = await api.updateAiBudget(orgId, { softLimitUsd: toLimit('b_soft'), hardLimitUsd: toLimit('b_hard') });
  if (res.ok) {
    notice.value = '';
    aiBudgetDialogOpen.value = false;
    showToast('Budget saved.');
    await loadAiUsage();
  } else notice.value = res.error ?? 'Save failed.';
}

export async function answerFormClient(root: HTMLElement, formId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  const forms = (workspace.value?.forms ?? []) as FormRow[];
  const form = forms.find((f) => f.id === formId);
  if (orgId === undefined || orgId === null || demandId === undefined || form === undefined) return;

  const answers: Record<string, string | number | boolean> = {};
  for (const f of form.schema.fields) {
    const el = root.querySelector(`[name="f_${formId}_${f.key}"]`);
    if (el === null) continue;
    if (f.type === 'boolean') answers[f.key] = elChecked(el);
    else {
      const v = elValue(el);
      if (f.type === 'number' || f.type === 'currency') {
        if (v !== '') answers[f.key] = Number(v);
      } else if (v !== '') answers[f.key] = v;
    }
  }
  const before = await analysesBeforeFormSubmit(orgId, demandId);
  const response = await fetch(`/api/orgs/${orgId}/forms/${formId}/answer`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ answers } satisfies FormAnswer),
  });
  if (!response.ok) {
    notice.value = 'Could not submit answers.';
    return;
  }
  const result = (await response.json().catch(() => ({}))) as Partial<FormAnswerResponse>;
  questionsDraft.value = null;
  await reopenDemandIfShown(demandId);
  closeQuestionsDialogIfDone();
  if (result.resumedJobId !== undefined) showToast('Re-checking prices with your answers.');
  await followResumedRun({ orgId, demandId }, before, result.resumedJobId);
}

/**
 * The pricing-check versions before a form submit, so a run the submit starts can
 * be told apart from earlier ones. Requesters cannot read pricing checks.
 */
async function analysesBeforeFormSubmit(orgId: string, demandId: string): Promise<AnalysisRowT[] | undefined> {
  if (isRequester()) return undefined;
  const list = await api.listAnalyses(orgId, demandId);
  return list.ok && list.data !== null ? list.data.analyses : undefined;
}

export async function skipFormClient(formId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === undefined || orgId === null || demandId === undefined) return;
  const before = await analysesBeforeFormSubmit(orgId, demandId);
  const response = await fetch(`/api/orgs/${orgId}/forms/${formId}/skip`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!response.ok) {
    notice.value = 'Could not skip questions.';
    return;
  }
  const result = (await response.json().catch(() => ({}))) as Partial<FormSkipResponse>;
  questionsDraft.value = null;
  await reopenDemandIfShown(demandId);
  closeQuestionsDialogIfDone();
  await followResumedRun({ orgId, demandId }, before, result.resumedJobId);
}

/**
 * Load the current member's server-backed draft before opening the dialog. The
 * next open form for the member's role comes first; otherwise a skipped
 * pricing-check clarification behind the shown stated assumptions opens, so it
 * can still be answered and re-checked (FR-3.12, HS2-54N88P).
 */
export async function openQuestionsDialogClient(): Promise<void> {
  const orgId = me.value?.organizationId;
  const requester = isRequester();
  const forRole = (candidate: FormRow): boolean =>
    candidate.schema.targetRole === undefined ||
    candidate.schema.targetRole === 'any' ||
    candidate.schema.targetRole === (requester ? 'requester' : 'reviewer');
  const forms = ((workspace.value?.forms ?? []) as FormRow[]).filter(forRole);
  const assumptionFormIds = new Set(
    (analysisReport.value?.analysis.summary?.openQuestions ?? []).map((question) => question.formId),
  );
  const form =
    forms.find((candidate) => candidate.status === 'open') ??
    forms.find((candidate) => assumptionFormIds.has(candidate.id) && formAcceptsAnswers(candidate));
  if (orgId === null || orgId === undefined || form === undefined) return;
  const result = await api.getFormDraft(orgId, form.id);
  if (!result.ok || result.data === null) {
    notice.value = result.error ?? 'Could not load the saved draft.';
    return;
  }
  questionsDraft.value = { formId: form.id, answers: result.data.answers ?? {} };
  questionsDialogOpen.value = true;
}

/** Save partial answers for the current member and close after confirmation. */
export async function saveQuestionsDraftClient(root: HTMLElement, formId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const forms = (workspace.value?.forms ?? []) as FormRow[];
  const form = forms.find((f) => f.id === formId);
  if (orgId === null || orgId === undefined || form === undefined) return;
  const values: QuestionsDraft = {};
  for (const f of form.schema.fields) {
    const el = root.querySelector(`[name="f_${formId}_${f.key}"]`);
    if (el === null) continue;
    if (f.type === 'boolean') {
      if (elChecked(el)) values[f.key] = true;
    } else {
      const v = f.type === 'select' ? selectValue(el) : elValue(el);
      if (v !== '') values[f.key] = v;
    }
  }
  const result = await api.saveFormDraft(orgId, formId, values);
  if (!result.ok || result.data === null) {
    notice.value = result.error ?? 'Could not save draft.';
    return;
  }
  questionsDraft.value = { formId, answers: result.data.answers };
  notice.value = '';
  showToast('Draft saved. Your answers are available on your other devices.');
  questionsDialogOpen.value = false;
}

// --- Intake (Flow F2) handlers ---

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

let multifileBatch: MultifileBatch | null = null;

export function cancelMultifileBatch(): void {
  multifileBatch = null;
  multifileUploadProgress.value = null;
}

function reconcileMultifileBatch(demandId: string): void {
  const batch = multifileBatch;
  const ws = workspace.value;
  if (batch === null || batch.demandId !== demandId || ws === null || shownDemandId() !== demandId) return;
  const documents = Object.values((ws.filesByKind ?? {}) as Record<string, IntakeDoc[]>).flat();
  const result = multifileBatchResult(batch, documents, ((ws.lineItems ?? []) as unknown[]).length);
  if (result === 'waiting') return;
  cancelMultifileBatch();
  if (result === 'ready' && canOpenDemandSegment('summary')) {
    editorSegment.value = 'summary';
    void loadQuoteMatrix(undefined, { fresh: true });
  } else if (result === 'no-lines') {
    notice.value = 'The uploaded files produced no line items. Review the Documents before running a pricing check.';
  }
}

export async function uploadIntakeFiles(files: File[], kind: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const ws = workspace.value;
  const newDemand =
    ws !== null &&
    ((ws.lineItems ?? []) as unknown[]).length === 0 &&
    Object.values((ws.filesByKind ?? {}) as Record<string, unknown[]>).every((group) => group.length === 0);
  const fastPath = kind === 'auto' && files.length > 1 && newDemand;
  cancelMultifileBatch();
  if (fastPath) {
    multifileBatch = { demandId: c.demandId, documentIds: [], uploading: true };
    multifileUploadProgress.value = { uploaded: 0, total: files.length };
  }
  notice.value = '';
  for (const file of files) {
    const res = await api.uploadDocument(c.orgId, c.demandId, file, kind);
    if (!res.ok) notice.value = res.error ?? 'Upload failed.';
    if (fastPath && multifileBatch?.demandId === c.demandId) {
      if (res.ok && res.data !== null) multifileBatch.documentIds.push(res.data.id);
      multifileUploadProgress.value = {
        uploaded: (multifileUploadProgress.value?.uploaded ?? 0) + 1,
        total: files.length,
      };
    }
  }
  if (fastPath && multifileBatch?.demandId === c.demandId) {
    multifileBatch.uploading = false;
    multifileUploadProgress.value = null;
    if (multifileBatch.documentIds.length === 0) cancelMultifileBatch();
  }
  // Extraction runs asynchronously in the worker; poll a few times for results while the
  // demand stays on screen (the user may move on while the upload finishes).
  await reloadDemand(c.demandId);
  for (let i = 0; i < 4 && shownDemandId() === c.demandId; i++) {
    await sleep(700);
    await reloadDemand(c.demandId);
  }
}

function currentDocument(documentId: string): IntakeDoc | undefined {
  const groups = (workspace.value?.filesByKind ?? {}) as Record<string, IntakeDoc[]>;
  return Object.values(groups)
    .flat()
    .find((document) => document.id === documentId);
}

let annotationLoadRequest = 0;
// Read through a function after awaits: the signal may have changed while suspended.
function currentDocumentPreview(): typeof documentPreview.value {
  return documentPreview.value;
}

/** Resolve a short-lived authorized URL, then preview images or download files. */
export async function openDocumentClient(documentId: string, forceDownload = false): Promise<void> {
  const orgId = me.value?.organizationId;
  const document = currentDocument(documentId);
  if (orgId === null || orgId === undefined || document === undefined) return;
  const previewImage = !forceDownload && isImageDocument(document);
  const requestId = previewImage ? ++annotationLoadRequest : undefined;
  const demandId = currentDemandId();
  const previousPreview = documentPreview.value;
  const result = await api.getDocumentUrl(orgId, documentId);
  if (
    previewImage &&
    (requestId !== annotationLoadRequest ||
      demandId !== currentDemandId() ||
      (previousPreview !== null && documentPreview.value === null))
  )
    return;
  if (!result.ok || result.data === null) {
    notice.value = result.error ?? 'Could not open the file.';
    return;
  }
  if (previewImage) {
    // A fresh gallery selection starts at fit size (HS2-324319).
    documentPreview.value = { documentId, url: result.data.url, annotationsLoading: true };
    documentAnnotations.value = [];
    if (demandId !== undefined) {
      const annotations = await api.getDocumentAnnotations(orgId, demandId, documentId);
      const current = currentDocumentPreview();
      if (requestId === annotationLoadRequest && current?.documentId === documentId) {
        documentAnnotations.value = annotations.ok ? (annotations.data?.annotations ?? []) : [];
        documentPreview.value = { ...current, annotationsLoading: false, annotationLoadFailed: !annotations.ok };
      }
    }
    return;
  }
  const anchor = globalThis.document.createElement('a');
  anchor.href = result.data.url;
  anchor.download = document.filename;
  anchor.style.display = 'none';
  globalThis.document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/** Open a Knowledge Base file using the same signed URL and image gallery as demand files. */
export async function openLibraryDocumentClient(documentId: string, forceDownload = false): Promise<void> {
  const orgId = me.value?.organizationId;
  const document = libraryDocs.value.find((candidate) => candidate.id === documentId);
  if (orgId === null || orgId === undefined || document === undefined) return;
  const projectId = libraryScope.value;
  if (document.projectId !== (projectId === '' ? null : projectId)) return;
  const requestId = ++annotationLoadRequest;
  const result = await api.getDocumentUrl(orgId, documentId);
  if (view.value !== 'library' || libraryScope.value !== projectId || requestId !== annotationLoadRequest) return;
  if (!result.ok || result.data === null) {
    notice.value = result.error ?? 'Could not open the file.';
    return;
  }
  if (!forceDownload && isImageDocument(document)) {
    documentPreview.value = { documentId, url: result.data.url, source: 'library' };
    documentAnnotations.value = [];
    return;
  }
  startDownload(result.data.url, document.filename);
}

export async function addDocumentAnnotationClient(body: string, point: { x: number; y: number }): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  const active = documentPreview.value;
  if (
    orgId === undefined ||
    orgId === null ||
    demandId === undefined ||
    active === null ||
    active.annotationSaving === true ||
    active.annotationsLoading === true ||
    active.annotationLoadFailed === true
  )
    return;
  const requestId = annotationLoadRequest;
  documentPreview.value = { ...active, annotationSaving: true, annotationMessage: undefined };
  const result = await api.addDocumentAnnotation(orgId, demandId, active.documentId, { body, ...point });
  const current = currentDocumentPreview();
  if (current?.documentId !== active.documentId || requestId !== annotationLoadRequest) return;
  if (!result.ok || result.data === null) {
    documentPreview.value = {
      ...current,
      annotationSaving: false,
      annotationMessage: result.error ?? 'Could not save annotation.',
    };
    return;
  }
  documentAnnotations.value = [...documentAnnotations.value, result.data];
  documentPreview.value = { ...current, annotationSaving: false, point: undefined };
}

export async function deleteDocumentAnnotationClient(annotationId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  const active = documentPreview.value;
  if (
    orgId === undefined ||
    orgId === null ||
    demandId === undefined ||
    active === null ||
    active.annotationSaving === true
  )
    return;
  const requestId = annotationLoadRequest;
  documentPreview.value = { ...active, annotationSaving: true, annotationMessage: undefined };
  const result = await api.deleteDocumentAnnotation(orgId, demandId, active.documentId, annotationId);
  const current = currentDocumentPreview();
  if (current?.documentId !== active.documentId || requestId !== annotationLoadRequest) return;
  if (!result.ok) {
    documentPreview.value = {
      ...current,
      annotationSaving: false,
      annotationMessage: result.error ?? 'Could not remove annotation.',
    };
    return;
  }
  documentPreview.value = { ...current, annotationSaving: false };
  documentAnnotations.value = documentAnnotations.value.filter((annotation) => annotation.id !== annotationId);
}

export async function moveDocumentAnnotationClient(
  annotationId: string,
  point: { x: number; y: number },
): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  const active = documentPreview.value;
  const annotation = documentAnnotations.value.find((item) => item.id === annotationId);
  const actor = me.value;
  if (
    orgId === undefined ||
    orgId === null ||
    demandId === undefined ||
    active === null ||
    active.annotating !== true ||
    active.annotationSaving === true ||
    active.annotationsLoading === true ||
    active.annotationLoadFailed === true ||
    annotation === undefined ||
    actor === null ||
    (annotation.authorMemberId !== actor.memberId &&
      !actor.roles.some((role) => role === 'reviewer' || role === 'admin')) ||
    !Number.isFinite(point.x) ||
    !Number.isFinite(point.y) ||
    point.x < 0 ||
    point.x > 1 ||
    point.y < 0 ||
    point.y > 1
  )
    return;
  const requestId = annotationLoadRequest;
  const previous = documentAnnotations.value;
  documentAnnotations.value = previous.map((item) => (item.id === annotationId ? { ...item, ...point } : item));
  documentPreview.value = { ...active, annotationSaving: true, annotationMessage: undefined };
  const result = await api.moveDocumentAnnotation(orgId, demandId, active.documentId, annotationId, point);
  const current = currentDocumentPreview();
  if (current?.documentId !== active.documentId || requestId !== annotationLoadRequest) return;
  if (!result.ok || result.data === null) {
    documentAnnotations.value = previous;
    documentPreview.value = {
      ...current,
      annotationSaving: false,
      annotationMessage: result.error ?? 'Could not move annotation.',
    };
    return;
  }
  const moved = result.data;
  documentAnnotations.value = documentAnnotations.value.map((item) => (item.id === annotationId ? moved : item));
  documentPreview.value = { ...current, annotationSaving: false };
}

export async function deleteDocumentsClient(documentIds: string[]): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  cancelMultifileBatch();
  for (const documentId of documentIds) {
    const result = await api.deleteDocument(orgId, demandId, documentId);
    if (!result.ok) {
      notice.value = result.error ?? 'Could not delete the file.';
      return;
    }
  }
  selectedDocumentIds.value = [];
  documentSelectionAnchor.value = '';
  documentPreview.value = null;
  await reopenDemandIfShown(demandId);
}

/** Move one Supplemental file into the chosen library and refresh the demand. */
export async function promoteDocumentClient(documentId: string, projectId?: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  cancelMultifileBatch();
  const result = await api.promoteDocument(orgId, documentId, projectId);
  if (!result.ok) {
    notice.value = result.error ?? 'Could not promote the file.';
    return;
  }
  selectedDocumentIds.value = [];
  documentSelectionAnchor.value = '';
  documentPreview.value = null;
  await reopenDemandIfShown(demandId);
  editorSegment.value = 'files';
  notice.value = '';
  const destination =
    projectId === undefined
      ? 'Organization Knowledge Base'
      : `${libraryProjects.value.find((project) => project.id === projectId)?.name ?? 'Project'} Knowledge Base`;
  showToast(`File promoted to ${destination}.`);
}

export async function replaceDocumentClient(documentId: string, file: File): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return;
  cancelMultifileBatch();
  const result = await api.replaceDocument(orgId, demandId, documentId, file);
  if (!result.ok) {
    notice.value = result.error ?? 'Could not replace the file.';
    return;
  }
  await reopenDemandIfShown(demandId);
  editorSegment.value = 'files';
  selectedDocumentIds.value = [documentId];
  documentSelectionAnchor.value = documentId;
  notice.value = '';
  showToast('File replaced with the new version.');
  for (let i = 0; i < 4; i++) {
    await sleep(700);
    await refreshWorkspace();
  }
}

/** Reclassify one or more misplaced demand documents, preserving each file's
 * identity and refreshing the destination segment after extraction is queued. */
export async function moveDocumentsClient(
  documentIds: string[],
  targetKind: 'demand' | 'quotation' | 'supporting',
): Promise<void> {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined || documentIds.length === 0) return;
  cancelMultifileBatch();
  for (const documentId of documentIds) {
    const result = await api.moveDocument(orgId, demandId, documentId, targetKind);
    if (!result.ok) {
      notice.value = result.error ?? 'Could not move the file.';
      return;
    }
  }
  selectedDocumentIds.value = [];
  documentSelectionAnchor.value = '';
  documentPreview.value = null;
  await reopenDemandIfShown(demandId);
  editorSegment.value = 'files';
  let label: string;
  switch (targetKind) {
    case 'demand':
      label = 'Line Items';
      break;
    case 'quotation':
      label = 'Quotes';
      break;
    case 'supporting':
      label = 'Supplemental';
      break;
  }
  notice.value = '';
  showToast(
    documentIds.length === 1
      ? `File type changed to ${label}.`
      : `${String(documentIds.length)} file types changed to ${label}.`,
  );
  for (let i = 0; i < 4; i++) {
    await sleep(700);
    await refreshWorkspace();
  }
}

export async function saveLineItemEditor(
  lineId: string | null,
  values: {
    description: string;
    quantity: string;
    unit: string;
    hsCode: string;
    hsCodeDutyPct: string;
    hsCodeEdited: boolean;
  },
): Promise<void> {
  const c = intakeCtx();
  const description = values.description.trim();
  if (c === null || description === '') {
    notice.value = description === '' ? 'Line item description is required.' : '';
    return;
  }
  // No normalizedDescription is sent: the assistant normalizes manual entries in the
  // background (HS2-6ZNBWY).
  const hsCode = values.hsCode.trim();
  const hsCodeDutyPct = values.hsCodeDutyPct.trim();
  if (values.hsCodeEdited && hsCode === '' && hsCodeDutyPct !== '') {
    notice.value = 'Enter an HS code before setting a customs duty.';
    return;
  }
  const body = {
    description,
    quantity: values.quantity.trim() === '' ? undefined : values.quantity.trim(),
    unit: values.unit.trim(),
    ...(values.hsCodeEdited
      ? {
          hsCode: hsCode === '' ? null : hsCode,
          hsCodeDutyPct: hsCode === '' || hsCodeDutyPct === '' ? null : hsCodeDutyPct,
        }
      : {}),
  };
  const res =
    lineId === null
      ? await api.addLineItem(c.orgId, c.demandId, body)
      : await api.updateLineItem(c.orgId, c.demandId, lineId, body);
  if (!res.ok) {
    notice.value = res.error ?? 'Could not save the line item.';
    return;
  }
  notice.value = '';
  lineEditorDialog.value = null;
  await reloadDemand(c.demandId);
}

/** Add one line item per non-empty pasted line (HS2-TBFF8Q: paste into the box). */
export async function addIntakePastedText(text: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const items = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
  if (items.length === 0) return;
  for (const description of items) await api.addLineItem(c.orgId, c.demandId, { description });
  await reloadDemand(c.demandId);
}

/** Remove one or more line items (row kebab "Remove" and batch remove, HS2-1F4215). */
export async function removeLines(lineIds: string[]): Promise<void> {
  const c = intakeCtx();
  if (c === null || lineIds.length === 0) return;
  for (const id of lineIds) await api.deleteLineItem(c.orgId, c.demandId, id);
  selectedLineIds.value = selectedLineIds.value.filter((id) => !lineIds.includes(id));
  if (!selectedLineIds.value.includes(lineSelectionAnchor.value)) lineSelectionAnchor.value = '';
  await reloadDemand(c.demandId);
}

/** Record the same decision action across one or more lines (row kebab / right-click
 *  menu and batch bar, HS2-1F4215). approve carries an approved price, negotiate a
 *  target price; the others record directly with no price. Clears the selection and
 *  closes the dialog, then refreshes decisions + workspace status. */
/** The cheapest quote line for a line from the loaded quote matrix (by delivered
 *  base price), used as the default chosen vendor on a decision (HS2-ZP1D0Z). */
function bestQuoteLineFor(lineId: string): string | undefined {
  const m = quoteMatrix.value;
  const row = m?.cells[lineId];
  if (row === undefined) return undefined;
  let best: { quoteLineId: string; basePrice: number } | undefined;
  for (const cell of Object.values(row)) {
    if (cell.basePrice == null) continue;
    if (best === undefined || cell.basePrice < best.basePrice)
      best = { quoteLineId: cell.quoteLineId, basePrice: cell.basePrice };
  }
  return best?.quoteLineId;
}

export async function recordLineDecisions(
  lineIds: string[],
  action: string,
  price: string | null,
  note: string | null,
  chosenQuoteLineId?: string,
  selectionReason?: string,
  recommendations?: RecommendedBatchDecision[],
): Promise<void> {
  const c = intakeCtx();
  // Narrow the raw action string to the DecisionAction enum before building the body.
  if (c === null || lineIds.length === 0 || !(DECISION_ACTIONS as readonly string[]).includes(action)) return;
  const decisionAction = action as DecisionAction;
  const body: RecordDecision = { action: decisionAction };
  if (note !== null && note !== '') body.note = note;
  if (price !== null && price !== '') {
    if (decisionAction === 'approve') body.approvedUnitPrice = Number(price);
    else body.targetPrice = Number(price);
  }
  // Record the chosen vendor's quote line so savings roll up by vendor (HS2-ZP1D0Z):
  // the single-line dialog can override; otherwise default to the line's best quote.
  const withVendor = decisionAction === 'approve' || decisionAction === 'negotiate';
  let ok = true;
  for (const id of lineIds) {
    const perLine: RecordDecision = { ...body };
    const recommendation = recommendations?.find((candidate) => candidate.lineId === id);
    if (recommendation !== undefined) {
      if (decisionAction === 'approve') perLine.approvedUnitPrice = recommendation.price;
      else if (decisionAction === 'negotiate') perLine.targetPrice = recommendation.price;
    }
    if (withVendor) {
      const override = lineIds.length === 1 && chosenQuoteLineId !== undefined && chosenQuoteLineId !== '';
      const chosen = recommendation?.chosenQuoteLineId ?? (override ? chosenQuoteLineId : bestQuoteLineFor(id));
      if (chosen !== undefined) perLine.chosenQuoteLineId = chosen;
      // A reason is only meaningful for a single-line override to a non-best quote.
      if (override && selectionReason !== undefined && selectionReason !== '')
        perLine.selectionReason = selectionReason;
    }
    const res = await api.recordDecision(c.orgId, c.demandId, id, perLine);
    if (!res.ok) {
      ok = false;
      notice.value = res.error ?? 'Could not record the decision.';
    }
  }
  if (ok) notice.value = '';
  lineDecisionDialog.value = null;
  selectedLineIds.value = [];
  lineSelectionAnchor.value = '';
  await loadDecisions();
  await reloadDemand(c.demandId);
}

/** Reopen a rejected demand with an update (HS2-Z6MFSA): note, clear rejections, back to staff. */
export async function reopenWithUpdateClient(update: string): Promise<void> {
  const c = intakeCtx();
  if (c === null || update.trim() === '') return;
  const res = await fetch(`/api/orgs/${c.orgId}/demands/${c.demandId}/reopen-with-update`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ update }),
  });
  if (!res.ok) {
    notice.value = 'Could not reopen this demand.';
    return;
  }
  reopenUpdateOpen.value = false;
  notice.value = '';
  await Promise.all([loadDecisions(), reloadDemand(c.demandId), loadDemands()]);
}

/** Ask the requester to clarify one line (HS2-R5H22P): note, flag, undecide, and return the demand. */
export async function requestLineClarificationClient(lineId: string, question: string): Promise<void> {
  const c = intakeCtx();
  if (c === null || question.trim() === '') return;
  const res = await fetch(`/api/orgs/${c.orgId}/demands/${c.demandId}/line-items/${lineId}/clarify`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ question }),
  });
  if (!res.ok) {
    notice.value = 'Could not ask for clarification.';
    return;
  }
  clarifyDialog.value = null;
  notice.value = '';
  await Promise.all([loadDecisions(), reloadDemand(c.demandId), loadDemands()]);
}

export async function clearLineDecision(lineId: string): Promise<void> {
  const c = intakeCtx();
  if (c === null || lineId === '') return;
  const res = await api.clearDecision(c.orgId, c.demandId, lineId);
  if (!res.ok) {
    notice.value = res.error ?? 'Could not clear the decision.';
    return;
  }
  notice.value = '';
  await Promise.all([loadDecisions(), reloadDemand(c.demandId)]);
}

export async function saveIntakeCell(lineId: string, fieldName: string, value: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  await api.updateLineItem(c.orgId, c.demandId, lineId, { [fieldName]: value });
  await reloadDemand(c.demandId);
}

/** Auto-save one demand header field on blur (title / purpose / neededBy). No
 *  Save button — every field persists as the user leaves it (HS2-1ZGN3H). */
export async function saveIntakeField(name: string, value: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const v = value.trim();
  let patch: Record<string, unknown>;
  if (name === 'title') patch = { title: v === '' ? 'Untitled demand' : v };
  else if (name === 'neededBy') patch = { neededBy: v === '' ? null : v };
  else if (name === 'purpose') patch = { purpose: v === '' ? null : v };
  else return;
  await api.updateDemand(c.orgId, c.demandId, patch);
  await reloadDemand(c.demandId);
}

/**
 * Pull the open demand's notes and reads after this member posted or edited one (HS2-JRQT8A). Unlike
 * a background refresh this runs while a dialog is open (the line dialog shows its notes), and it
 * replaces only the notes, so nothing else in an open editor re-renders from new data.
 */
async function reloadOpenNotes(orgId: string, demandId: string): Promise<void> {
  const res = await api.getDemand(orgId, demandId);
  const ws = workspace.value;
  if (!res.ok || ws === null || currentDemandId() !== demandId) return;
  const fresh = res.data as { notes?: unknown; noteReads?: unknown };
  workspace.value = { ...ws, notes: fresh.notes ?? [], noteReads: fresh.noteReads ?? {} };
}

/** Append a note to the open demand's list, or one line's (HS2-E33XQD, HS2-JRQT8A). */
export async function addNoteClient(body: string, lineItemId: string | null): Promise<boolean> {
  const c = intakeCtx();
  if (c === null || body.trim() === '') return false;
  const res = await fetch(`/api/orgs/${c.orgId}/demands/${c.demandId}/notes`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ body, lineItemId }),
  });
  if (!res.ok) notice.value = 'Could not add the note.';
  await reloadOpenNotes(c.orgId, c.demandId);
  return res.ok;
}

/** Save an edit to the author's newest note while its minute lasts (HS2-E33XQD). */
export async function saveNoteEditClient(noteId: string, body: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const res = await fetch(`/api/orgs/${c.orgId}/demands/${c.demandId}/notes/${noteId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  editingNoteId.value = null;
  if (!res.ok) notice.value = 'This note can no longer be edited; add a new note instead.';
  await reloadOpenNotes(c.orgId, c.demandId);
}

/**
 * Mark the open demand's notes list (or one line's) read for this member (HS2-E33XQD). The read
 * marker moves at once to the newest note in the list, so unread banners and badges clear without
 * waiting; the server records the read at its own clock.
 */
export async function markNotesReadClient(lineItemId: string | null): Promise<void> {
  if (actingReadOnly()) return;
  const c = intakeCtx();
  const ws = workspace.value;
  if (c === null || ws === null) return;
  const notes = notesInScope((ws.notes ?? []) as DemandNote[], lineItemId);
  const newest = notes.at(-1)?.createdAt;
  if (newest !== undefined) {
    const reads = { ...((ws.noteReads ?? {}) as Record<string, string>), [noteScopeKey(lineItemId)]: newest };
    workspace.value = { ...ws, noteReads: reads };
  }
  await fetch(`/api/orgs/${c.orgId}/demands/${c.demandId}/notes/read`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ lineItemId }),
  });
}

/** The open demand's demand-level notes this member has not read (HS2-E33XQD). */
export function unreadDemandNotes(): DemandNote[] {
  const ws = workspace.value;
  if (ws === null) return [];
  const notes = notesInScope((ws.notes ?? []) as DemandNote[], null);
  const readAt = ((ws.noteReads ?? {}) as Record<string, string>)[DEMAND_NOTES_SCOPE];
  return unreadNotes(notes, readAt, me.value?.memberId);
}

export async function confirmIntake(): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const res = await api.confirmDemand(c.orgId, c.demandId);
  if (res.ok) await reopenDemandIfShown(c.demandId);
  else notice.value = res.error ?? 'Could not confirm.';
}

// --- Quotes (Flow F3) handlers ---

export async function uploadQuoteFiles(files: File[], kind: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  notice.value = '';
  for (const file of files) {
    const res = await api.uploadDocument(c.orgId, c.demandId, file, kind);
    if (!res.ok) notice.value = res.error ?? 'Upload failed.';
  }
  // Refresh the shared workspace immediately so the one header-level processing
  // banner represents quote uploads too; keep it current while the matrix polls.
  await reloadDemand(c.demandId);
  // Extraction runs asynchronously in the worker; poll a few times for results.
  await loadQuoteMatrix(undefined, { fresh: true });
  for (let i = 0; i < 5; i++) {
    await sleep(700);
    await reloadDemand(c.demandId);
    await loadQuoteMatrix(undefined, { fresh: true });
  }
  await reloadDemand(c.demandId); // refresh line-item counts / status
}

/** Accept the intake offer to reclassify a supporting document as a vendor
 *  quotation (HS2-FKJB32). Flips the document's kind and re-runs the same
 *  quote-extract pipeline a directly-uploaded quotation takes, then polls the
 *  price matrix (extraction runs async in the worker) so the new quotes appear
 *  under Quotes. The document row is refreshed too so the offer clears. */
export async function reclassifyDocAsQuote(docId: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  notice.value = '';
  const res = await api.reclassifyToQuotation(c.orgId, c.demandId, docId);
  if (!res.ok) {
    notice.value = res.error ?? 'Could not add this document as a quote.';
    return;
  }
  await reloadDemand(c.demandId); // clears the offer (kind is no longer supporting)
  await loadQuoteMatrix(undefined, { fresh: true });
  for (let i = 0; i < 5; i++) {
    await sleep(700);
    await loadQuoteMatrix(undefined, { fresh: true });
  }
  await reloadDemand(c.demandId);
}

/** Requeue a failed quotation without replacing its source file or prior rows. */
export async function retryQuotationExtractionClient(docId: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const res = await api.retryQuotationExtraction(c.orgId, c.demandId, docId);
  if (!res.ok) {
    notice.value = res.error ?? 'Could not retry quotation extraction.';
    return;
  }
  attachmentErrorsOpen.value = false;
  notice.value = '';
  showToast('Quotation extraction queued again.');
  await reloadDemand(c.demandId);
  for (let i = 0; i < 5; i++) {
    await sleep(700);
    await reloadDemand(c.demandId);
    await loadQuoteMatrix(undefined, { fresh: true });
  }
}

/** Save the add/edit-quotation dialog (HS2-0SV2XB). Add creates a manual quotation
 *  (header + per-line prices); edit patches an existing quotation's header. */
export async function saveQuoteDialog(root: HTMLElement): Promise<void> {
  const c = intakeCtx();
  const m = quoteMatrix.value;
  const dlg = quoteDialog.value;
  if (c === null || m === null || dlg === null) return;
  const vendorName = field(root, 'q-vendor');
  if (vendorName === '') {
    notice.value = 'Enter a vendor name.';
    return;
  }
  const currency = field(root, 'q-currency') || m.base;
  const priceBasis = field(root, 'q-basis') || undefined;
  if (dlg.mode === 'edit') {
    const res = await api.updateQuotation(c.orgId, c.demandId, dlg.quotationId, { vendorName, currency, priceBasis });
    if (res.ok) {
      notice.value = '';
      quoteDialog.value = null;
      await loadQuoteMatrix(undefined, { fresh: true });
    } else notice.value = res.error ?? 'Could not update the quotation.';
    return;
  }
  const built = buildManualQuotationInput({
    vendorName,
    currency,
    priceBasis,
    grandTotal: field(root, 'q-grand-total'),
    taxAmount: field(root, 'q-tax-amount'),
    freightAmount: field(root, 'q-freight-amount'),
    discountAmount: field(root, 'q-discount-amount'),
    taxBasis: selectValue(root.querySelector('kui-select[name="q-tax-basis"]')) || 'unknown',
    lines: [
      ...m.lineItems.map((li) => ({
        lineItemId: li.id,
        unitPrice: elValue(root.querySelector(`[data-mline="${li.id}"]`)).trim(),
        quantityOffered: field(root, `q-line-qty-${li.id}`),
        total: field(root, `q-line-total-${li.id}`),
      })),
      ...Array.from(root.querySelectorAll('[data-xline-desc]')).map((el) => {
        const i = el.getAttribute('data-xline-desc') ?? '';
        return {
          description: elValue(el).trim(),
          unitPrice: elValue(root.querySelector(`[data-xline-price="${i}"]`)).trim(),
          quantityOffered: field(root, `q-extra-qty-${i}`),
          total: field(root, `q-extra-total-${i}`),
        };
      }),
    ],
  });
  if (!built.ok) {
    notice.value = built.error;
    return;
  }
  const res = await api.addQuotation(c.orgId, c.demandId, built.value);
  if (res.ok) {
    notice.value = '';
    quoteDialog.value = null;
    await loadQuoteMatrix(undefined, { fresh: true });
    await reloadDemand(c.demandId);
  } else notice.value = res.error ?? 'Could not save the quotation.';
}

export async function setQuoteBasis(quotationId: string, priceBasis: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  await api.updateQuotation(c.orgId, c.demandId, quotationId, { priceBasis });
  await loadQuoteMatrix(undefined, { fresh: true });
}

export async function remapQuoteLineClient(quoteLineId: string, lineItemId: string | null): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  await api.remapQuoteLine(c.orgId, c.demandId, quoteLineId, lineItemId);
  await loadQuoteMatrix(undefined, { fresh: true });
}

/** Unmap price-matrix cells (HS2-QV3276): each quote line goes back to the unmatched
 *  list. There is no batch endpoint, so the per-line remaps run in parallel and the
 *  matrix reloads once; failures are reported together. */
export async function unmapQuoteLinesClient(quoteLineIds: readonly string[]): Promise<void> {
  const c = intakeCtx();
  if (c === null || quoteLineIds.length === 0) return;
  const results = await Promise.allSettled(
    quoteLineIds.map((quoteLineId) => api.remapQuoteLine(c.orgId, c.demandId, quoteLineId, null)),
  );
  await loadQuoteMatrix(undefined, { fresh: true });
  const failed = results.filter((result) => result.status === 'rejected' || !result.value.ok).length;
  if (failed > 0) {
    showToast(
      failed === quoteLineIds.length && failed === 1
        ? 'Could not unmap the cell.'
        : `Could not unmap ${String(failed)} of ${String(quoteLineIds.length)} cells.`,
      'danger',
    );
  }
}

export async function addQuoteLineAsLine(quoteLineId: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  await api.addQuoteLineAsLineItem(c.orgId, c.demandId, quoteLineId);
  await loadQuoteMatrix(undefined, { fresh: true });
  await reloadDemand(c.demandId);
}

export async function ignoreQuoteLineClient(quoteLineId: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  await api.ignoreQuoteLine(c.orgId, c.demandId, quoteLineId);
  await loadQuoteMatrix(undefined, { fresh: true });
}

export async function mergeVendorsClient(spec: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const [fromList, intoId] = spec.split(':');
  if (intoId === undefined || intoId === '') return;
  for (const fromId of (fromList ?? '').split(',').filter((s) => s !== '')) {
    await api.mergeVendors(orgId, fromId, intoId);
  }
  // Refresh whichever surface triggered the merge.
  if (view.value === 'vendors') await loadVendors();
  else await loadQuoteMatrix(undefined, { fresh: true });
}

// --- Analysis (Flow F4/F5) handlers ---

export async function runAnalysisClient(): Promise<void> {
  const c = intakeCtx();
  if (c === null || analysisRunning.value) return;
  await withAnalysisRun(c, async (task) => {
    // A re-run must wait for a newly created version rather than accepting the
    // already-terminal version currently at the head of the list.
    const previousIds = await analysisIdsBefore(c);
    if (!analysisRunGuard.isCurrent(task)) return;
    const res = await api.runAnalysis(c.orgId, c.demandId);
    if (!analysisRunGuard.isCurrent(task)) return;
    if (!res.ok) {
      notice.value = res.error ?? 'Could not start the pricing check.';
      return;
    }
    await followAnalysisRun(c, task, previousIds, { pausedIsTerminal: true });
  });
}

/** The ids of the demand's existing pricing-check versions, before a run starts. */
async function analysisIdsBefore(c: { orgId: string; demandId: string }): Promise<Set<string>> {
  const before = await api.listAnalyses(c.orgId, c.demandId);
  return new Set(before.ok && before.data !== null ? before.data.analyses.map((row) => row.id) : []);
}

/**
 * Run a pricing-check task under the run guard: the toolbar is busy and progress
 * shows until it ends, and only the latest task on the current demand may clear it.
 */
async function withAnalysisRun(
  c: { orgId: string; demandId: string },
  body: (task: DemandTask) => Promise<void>,
): Promise<void> {
  const task = analysisRunGuard.begin(c.demandId);
  notice.value = '';
  analysisRunning.value = true;
  analysisProgress.value = null;
  try {
    await body(task);
  } catch (error) {
    if (analysisRunGuard.isCurrent(task))
      notice.value = error instanceof Error ? error.message : 'Could not run the pricing check.';
  } finally {
    if (analysisRunGuard.isCurrent(task)) {
      analysisRunning.value = false;
      analysisProgress.value = null;
    }
  }
}

/**
 * Follow a started pricing-check run to a terminal state, then load its report
 * (HS2-MHCQ1Y, HS2-3XNHWH). Live per-line progress arrives over the demand SSE
 * stream and accumulates into analysisProgress; the wait resolves on
 * analysis.done (or a legacy analysis.waiting pause). An immediate status read
 * catches a run that finished before subscribing, and repeated status checks
 * recover if the stream drops during a long run.
 *
 * `previousIds` are the versions that existed before the run; the run's own
 * version is the newest one not among them. A legacy resume reuses the paused
 * version, so its caller leaves that id out and sets `pausedIsTerminal: false`.
 * A run that ends paused (a legacy `waiting` analysis) opens the questions dialog.
 */
async function followAnalysisRun(
  c: { orgId: string; demandId: string },
  task: DemandTask,
  previousIds: ReadonlySet<string>,
  opts: { pausedIsTerminal: boolean },
): Promise<void> {
  selectedAnalysisId.value = '';
  await waitForTerminalAnalysis({
    pollMs: 45_000,
    isCurrent: () => analysisRunGuard.isCurrent(task),
    subscribe: (finish) =>
      connectDemandEvents(c.orgId, c.demandId, (type, data) => {
        if (!analysisRunGuard.isCurrent(task)) {
          finish();
          return;
        }
        if (type === 'analysis.progress') {
          analysisProgress.value = applyAnalysisProgress(analysisProgress.value, data as AnalysisProgressEvent);
        } else if (type === 'analysis.done' || (opts.pausedIsTerminal && type === 'analysis.waiting')) {
          finish();
        }
      }),
    isTerminal: async () => {
      const list = await api.listAnalyses(c.orgId, c.demandId);
      return list.ok && list.data !== null && newTerminalAnalysis(list.data.analyses, previousIds, opts) !== undefined;
    },
  });
  if (!analysisRunGuard.isCurrent(task)) return;

  // The run reached a terminal state; load the finished report version.
  const list = await api.listAnalyses(c.orgId, c.demandId);
  if (!analysisRunGuard.isCurrent(task)) return;
  const latest = list.ok && list.data !== null ? newTerminalAnalysis(list.data.analyses, previousIds, opts) : undefined;
  if (list.ok && list.data !== null && latest !== undefined && latest.status !== 'waiting') {
    analysisList.value = list.data.analyses;
    selectedAnalysisId.value = latest.id;
    await loadAnalysisReport(latest.id, c.demandId);
    if (!analysisRunGuard.isCurrent(task)) return;
  }
  const demand = await api.getDemand(c.orgId, c.demandId);
  if (!analysisRunGuard.isCurrent(task)) return;
  if (demand.ok) workspace.value = demand.data;
  // A legacy run paused for answers: point straight at the questions instead of leaving a silent wait.
  if (latest?.status === 'waiting') void openQuestionsDialogClient();
}

/**
 * After a form answer or skip, follow the pricing-check run the server started
 * for it (`resumedJobId`, HS2-3XNHWH): a re-check with the answers, or a legacy
 * paused run resuming. Shows live progress and loads the finished report.
 */
async function followResumedRun(
  c: { orgId: string; demandId: string },
  before: AnalysisRowT[] | undefined,
  resumedJobId: string | undefined,
): Promise<void> {
  if (resumedJobId === undefined || before === undefined || analysisRunning.value) return;
  const paused = before.find((row) => row.status === 'waiting');
  const previousIds = new Set(before.filter((row) => row !== paused).map((row) => row.id));
  await withAnalysisRun(c, (task) =>
    followAnalysisRun(c, task, previousIds, { pausedIsTerminal: paused === undefined }),
  );
}

/** Confirm a line's AI-proposed HS code + duty, then reload the report (the
 *  confirmed duty changes the line's landed cost/verdict) and the workspace (so
 *  the badge flips to confirmed). FR-3.21 (HS2-ECVJNJ). */
export async function confirmHsCodeClient(lineId: string): Promise<void> {
  const c = intakeCtx();
  const analysisId = selectedAnalysisId.value;
  if (c === null || lineId === '' || analysisId === '') return;
  const res = await api.confirmHsCode(c.orgId, c.demandId, analysisId, lineId);
  if (!res.ok) {
    notice.value = res.error ?? 'Could not confirm the HS code.';
    return;
  }
  await Promise.all([loadAnalysisReport(analysisId), refreshWorkspace()]);
}

export async function addManualBenchmarkClient(root: HTMLElement): Promise<void> {
  const c = intakeCtx();
  const lineId = lineEvidenceDialog.value?.lineId;
  const analysisId = analysisReport.value?.analysis.id;
  if (c === null || lineId === undefined || analysisId === undefined || lineEvidenceSubmitting.value) return;
  const price = elValue(root.querySelector('[name="evidence-price"]'));
  const currency = elValue(root.querySelector('[name="evidence-currency"]'));
  const unit = elValue(root.querySelector('[name="evidence-unit"]'));
  const sourceUrl = elValue(root.querySelector('[name="evidence-url"]'));
  const sourceTitle = elValue(root.querySelector('[name="evidence-title"]'));
  const note = elValue(root.querySelector('[name="evidence-note"]'));
  const matchValue =
    root.querySelector<HTMLElement & { value?: string }>('[name="evidence-match"]')?.value ?? 'equivalent';
  const matchQuality = matchValue === 'exact' || matchValue === 'proxy' ? matchValue : 'equivalent';
  if (price === '' || currency === '' || unit === '' || (sourceUrl === '' && sourceTitle === '' && note === '')) {
    lineEvidenceMessage.value = 'Enter price, currency, unit, and either a source URL or remembered-price context.';
    return;
  }
  lineEvidenceSubmitting.value = true;
  lineEvidenceMessage.value = '';
  try {
    const res = await api.addBenchmark(c.orgId, c.demandId, analysisId, {
      lineItemId: lineId,
      price: Number(price),
      currency,
      unit,
      sourceUrl: sourceUrl === '' ? undefined : sourceUrl,
      sourceTitle: sourceTitle === '' ? undefined : sourceTitle,
      geography: elValue(root.querySelector('[name="evidence-geography"]')) || undefined,
      basis: elValue(root.querySelector('[name="evidence-basis"]')) || undefined,
      matchQuality,
      note: note === '' ? undefined : note,
    });
    if (!res.ok) {
      lineEvidenceMessage.value = res.error ?? 'Could not add the benchmark.';
      return;
    }
    await loadAnalysisReport(analysisId, c.demandId);
    lineEvidenceMessage.value = 'Benchmark added and line re-checked.';
  } catch (error) {
    lineEvidenceMessage.value = error instanceof Error ? error.message : 'Could not add the benchmark.';
  } finally {
    lineEvidenceSubmitting.value = false;
  }
}

// --- Decisions (Flow F5) handlers ---

export async function bulkApplyDecisions(lineIds: string[] = []): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const displayedAnalysis = analysisReport.value?.analysis;
  if (displayedAnalysis === undefined || !isFinishedAnalysisStatus(displayedAnalysis.status)) {
    notice.value = 'Wait for a completed pricing check before applying recommendations.';
    return;
  }
  const res = await api.bulkApplyDecisions(c.orgId, c.demandId, displayedAnalysis.id, lineIds);
  if (res.ok && res.data !== null) {
    notice.value = '';
    showToast(
      `Applied ${String(res.data.applied)} recommendation${res.data.applied === 1 ? '' : 's'}${res.data.skipped > 0 ? `, ${String(res.data.skipped)} skipped` : ''}.`,
    );
  } else notice.value = res.error ?? 'Could not apply recommendations.';
  await loadDecisions();
  await reloadDemand(c.demandId);
  selectedLineIds.value = [];
  lineSelectionAnchor.value = '';
}

export async function recordOutcomeClient(root: HTMLElement, lineId: string): Promise<void> {
  const c = intakeCtx();
  if (c === null) return;
  const price = elValue(root.querySelector(`[name="out_price_${lineId}"]`));
  if (price === '') {
    notice.value = 'Enter the final price paid.';
    return;
  }
  // An explicit final-vendor override; blank falls back to the decision's vendor (HS2-ZP1D0Z).
  const vendorId = selectValue(root.querySelector(`[name="out_vendor_${lineId}"]`));
  const res = await api.recordOutcome(c.orgId, c.demandId, lineId, {
    finalUnitPrice: Number(price),
    ...(vendorId !== '' ? { finalVendorId: vendorId } : {}),
  });
  if (res.ok) {
    notice.value = '';
    await loadDecisions();
    await reloadDemand(c.demandId); // refresh the status chip (→ closed)
  } else notice.value = res.error ?? 'Could not record the outcome.';
}

/** Save the Mark Ordered / Edit Order dialog (FR-5.18, HS2-HYDRJX). Dates are calendar days. */
export async function saveOrderClient(root: HTMLElement): Promise<void> {
  const c = intakeCtx();
  const dialog = orderDialog.value;
  if (c === null || dialog === null) return;
  const orderDate = elValue(root.querySelector('[name="order-date"]'));
  const expected = elValue(root.querySelector('[name="order-expected"]'));
  const vendorId = selectValue(root.querySelector('[name="order-vendor"]'));
  const res = await api.recordOrder(c.orgId, c.demandId, dialog.lineId, {
    poNumber: elValue(root.querySelector('[name="order-po"]')).trim(),
    ...(orderDate !== '' ? { orderedAt: orderDate } : {}),
    expectedDeliveryAt: expected === '' ? null : expected,
    // Blank keeps the order's vendor, or the decision's on a new order.
    ...(vendorId !== '' ? { vendorId } : {}),
    note: elValue(root.querySelector('[name="order-note"]')).trim(),
  });
  if (!res.ok) {
    notice.value = res.error ?? 'Could not save the order.';
    return;
  }
  notice.value = '';
  orderDialog.value = null;
  await reloadDemand(c.demandId);
}

/** Record the Receipt dialog's delivery, optionally closing the rest short (FR-5.19, HS2-6AJZPE). */
export async function saveReceiptClient(root: HTMLElement): Promise<void> {
  const c = intakeCtx();
  const dialog = receiptDialog.value;
  if (c === null || dialog === null) return;
  const quantity = Number(elValue(root.querySelector('[name="receipt-quantity"]')) || '0');
  const closeShort =
    root.querySelector<HTMLElement & { checked?: boolean }>('[name="receipt-close-short"]')?.checked === true;
  if (!(quantity > 0) && !closeShort) {
    notice.value = 'Enter the quantity received, or close the rest short.';
    root.querySelector<HTMLElement>('[name="receipt-quantity"]')?.focus();
    return;
  }
  const receivedAt = elValue(root.querySelector('[name="receipt-date"]'));
  const res = await api.recordReceipt(c.orgId, c.demandId, dialog.lineId, {
    quantityReceived: quantity > 0 ? quantity : 0,
    ...(receivedAt !== '' ? { receivedAt } : {}),
    note: elValue(root.querySelector('[name="receipt-note"]')).trim(),
    ...(closeShort
      ? { closeShort: true, closeShortReason: elValue(root.querySelector('[name="receipt-short-reason"]')).trim() }
      : {}),
  });
  if (!res.ok) {
    notice.value = res.error ?? 'Could not record the receipt.';
    return;
  }
  notice.value = '';
  receiptDialog.value = null;
  await loadDecisions();
  await reloadDemand(c.demandId);
}

/** Remove a mistaken receipt from the Receipt dialog's line; the dialog stays open. */
export async function deleteReceiptClient(receiptId: string): Promise<void> {
  const c = intakeCtx();
  const dialog = receiptDialog.value;
  if (c === null || dialog === null) return;
  const res = await api.deleteReceipt(c.orgId, c.demandId, dialog.lineId, receiptId);
  notice.value = res.ok ? '' : (res.error ?? 'Could not remove the receipt.');
  await reloadDemand(c.demandId);
}

/** Reopen an order closed short, so the rest is expected again. */
export async function reopenShortClient(): Promise<void> {
  const c = intakeCtx();
  const dialog = receiptDialog.value;
  if (c === null || dialog === null) return;
  const res = await api.reopenShort(c.orgId, c.demandId, dialog.lineId);
  notice.value = res.ok ? '' : (res.error ?? 'Could not reopen the order.');
  await reloadDemand(c.demandId);
}

/** Re-source the Order dialog's line: its order failed (FR-5.21, HS2-SFY5DY). */
export async function failOrderClient(root: HTMLElement): Promise<void> {
  const c = intakeCtx();
  const dialog = orderDialog.value;
  if (c === null || dialog === null) return;
  const reason = elValue(root.querySelector('[name="order-fail-reason"]')).trim();
  if (reason === '') {
    notice.value = 'Say what went wrong with the order.';
    root.querySelector<HTMLElement>('[name="order-fail-reason"]')?.focus();
    return;
  }
  const res = await api.failOrder(c.orgId, c.demandId, dialog.lineId, { reason });
  if (!res.ok) {
    notice.value = res.error ?? 'Could not re-source the line.';
    return;
  }
  notice.value = '';
  orderDialog.value = null;
  await loadDecisions();
  await reloadDemand(c.demandId);
}

/** Undo Ordered on the dialog's line. */
export async function clearOrderClient(): Promise<void> {
  const c = intakeCtx();
  const dialog = orderDialog.value;
  if (c === null || dialog === null) return;
  const res = await api.clearOrder(c.orgId, c.demandId, dialog.lineId);
  if (!res.ok) {
    notice.value = res.error ?? 'Could not undo the order.';
    return;
  }
  notice.value = '';
  orderDialog.value = null;
  await reloadDemand(c.demandId);
}

// --- Background library (Flow F9) handlers ---

const sleepLib = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export async function uploadBackgroundFile(root: HTMLElement, file: File | undefined): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined || file === undefined) return;
  notice.value = '';
  const category = elValue(root.querySelector('[name="lib-category"]')) || undefined;
  const title = elValue(root.querySelector('[name="lib-title"]')) || undefined;
  const projectId = libraryScope.value === '' ? undefined : libraryScope.value;
  const res = await api.uploadBackground(orgId, file, { category, title, projectId });
  if (!res.ok) {
    notice.value = res.error ?? 'Upload failed.';
    return;
  }
  const t = root.querySelector<HTMLElement & { value?: string }>('[name="lib-title"]');
  if (t !== null) t.value = '';
  // The summarize job runs asynchronously; poll a few times for the summary + facts.
  await refreshLibrary();
  for (let i = 0; i < 5; i++) {
    await sleepLib(700);
    await refreshLibrary();
  }
}

/** Load just the project list (the project settings tabs, HS2-91S2YF). */
export async function loadLibraryProjects(): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  (await listProjectsInOrder(orgId)).apply();
}

/** Save one Project › Info field, then refresh the project list (HS2-91S2YF). */
export async function saveProjectInfo(projectId: string, field: 'name' | 'description', raw: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined || projectId === '') return;
  const value = raw.trim();
  if (field === 'name' && value === '') {
    notice.value = 'A project needs a name.';
    await loadLibraryProjects();
    return;
  }
  const res = await api.updateProject(orgId, projectId, field === 'name' ? { name: value } : { description: value });
  if (res.ok) await loadLibraryProjects();
  else notice.value = res.error ?? 'Could not save the project.';
}

export async function newLibraryProject(): Promise<boolean> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return false;
  const name = window.prompt('New project name', libraryProjects.value.length === 0 ? 'Default' : '');
  if (name === null || name.trim() === '') return false;
  const res = await api.createProject(orgId, { name: name.trim() });
  if (res.ok && res.data !== null) {
    libraryScope.value = res.data.id;
    await loadLibrary();
    return true;
  }
  notice.value = res.error ?? 'Could not create the project.';
  return false;
}

export async function closeLibraryFactDialog(root: HTMLElement): Promise<void> {
  const dialog = root.querySelector<HTMLDialogElement>('dialog[data-action="lib-fact-dialog"]');
  if (dialog?.open === true) {
    const hidden = new Promise<void>((resolve) => {
      dialog.addEventListener(
        'close',
        () => {
          resolve();
        },
        { once: true },
      );
    });
    closeDialog(dialog);
    await hidden;
  }
  libraryFactDialogId.value = undefined;
}

export async function addLibraryFact(root: HTMLElement): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const input = root.querySelector<HTMLElement & { value?: string }>('[name="lib-fact-text"]');
  const text = elValue(input).trim();
  if (text === '') return;
  const projectId = libraryScope.value === '' ? undefined : libraryScope.value;
  const res = await api.addFact(orgId, { text, projectId });
  if (res.ok) {
    await closeLibraryFactDialog(root);
    await refreshLibrary();
  } else notice.value = res.error ?? 'Could not add the fact.';
}

export async function saveLibraryFact(root: HTMLElement): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const id = libraryFactDialogId.value;
  if (id === undefined) return;
  if (id === null) return addLibraryFact(root);
  const text = elValue(root.querySelector('[name="lib-fact-text"]')).trim();
  if (text === '') {
    notice.value = 'Enter a fact.';
    return;
  }
  const res = await api.updateFact(orgId, id, { text });
  if (res.ok) {
    await closeLibraryFactDialog(root);
    await refreshLibrary();
  } else notice.value = res.error ?? 'Could not save the fact.';
}

export async function acceptLibraryFact(root: HTMLElement, id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  // Persist any inline edit to the fact text before accepting.
  const edited = elValue(root.querySelector(`[data-fact-edit="${id}"]`)).trim();
  const current = libraryFacts.value.find((f) => f.id === id)?.text;
  if (edited !== '' && edited !== current) await api.updateFact(orgId, id, { text: edited });
  await api.acceptFact(orgId, id);
  await refreshLibrary();
}

export async function deleteLibraryFact(id: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  await api.deleteFact(orgId, id);
  await refreshLibrary();
}

export async function archiveLibraryDoc(spec: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const [docId, flag] = spec.split(':');
  if (docId === undefined) return;
  await api.archiveDoc(orgId, docId, flag === '1');
  await refreshLibrary();
}

export async function showLibraryRevisions(documentId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId == null) return;
  const result = await api.listDocumentRevisions(orgId, documentId);
  if (!result.ok || result.data === null) {
    notice.value = result.error ?? 'Could not load document history.';
    return;
  }
  libraryRevisions.value = { documentId, revisions: result.data.revisions };
}

export async function replaceLibraryRevision(documentId: string, file: File): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId == null) return;
  const result = await api.reviseBackground(orgId, documentId, file);
  if (!result.ok) {
    notice.value = result.error ?? 'Could not replace document.';
    return;
  }
  libraryRevisions.value = null;
  await refreshLibrary();
}

export async function downloadLibraryRevision(documentId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const revision = libraryRevisions.value?.revisions.find((row) => row.id === documentId);
  if (orgId == null || revision === undefined) return;
  const result = await api.getDocumentUrl(orgId, documentId);
  if (!result.ok || result.data === null) {
    notice.value = result.error ?? 'Could not open the revision.';
    return;
  }
  startDownload(result.data.url, revision.filename);
}

/** A member with no staff role sees a price-redacted chat and cannot apply cards. */
/** Ensure there is an active conversation to post into, creating one if needed. */
export async function ensureConversation(): Promise<string | undefined> {
  if (chatActiveId.value !== '') return chatActiveId.value;
  const orgId = me.value?.organizationId;
  const target = chatTarget.value;
  if (orgId === null || orgId === undefined || target === null) return undefined;
  const res = await api.createConversation(orgId, target);
  if (res.ok && res.data !== null) {
    chatActiveId.value = res.data.id;
    return res.data.id;
  }
  return undefined;
}

export async function sendChat(root: HTMLElement): Promise<void> {
  const orgId = me.value?.organizationId;
  const input = root.querySelector<HTMLElement & { value?: string }>('[name="chat-input"]');
  const text = elValue(input).trim();
  if (orgId === null || orgId === undefined || chatTarget.value === null || text === '' || chatSending.value) return;
  const cid = await ensureConversation();
  if (cid === undefined) {
    notice.value = 'Could not start the conversation.';
    return;
  }
  // Make sure this conversation is streaming before the reply lands; after New Chat the open stream can
  // still belong to the previous chat (HS2-ZVVES3). This reloads the transcript from the server, so it
  // runs before the optimistic message is appended or it would wipe it (HS2-9VQ20R).
  if (chatStream === undefined || chatStreamId !== cid) await loadConversation(cid);
  chatSending.value = true;
  chatLiveActivity.value = 'Thinking…';
  // Optimistically show the user's message; the assistant reply arrives via SSE.
  chatMessages.value = [
    ...chatMessages.value,
    {
      id: `tmp-${String(Date.now())}`,
      role: 'user',
      content: { type: 'text', text },
      citations: [],
      createdEntities: [],
      createdAt: new Date().toISOString(),
    },
  ];
  if (input !== null) input.value = '';
  const res = await api.sendChatMessage(orgId, cid, text);
  if (!res.ok) {
    chatSending.value = false;
    chatLiveActivity.value = '';
    notice.value = res.error ?? 'Could not send the message.';
    return;
  }
  // The reply normally arrives via SSE (chat.done). Poll as a fallback so a
  // missed event (e.g. a connection gap) still surfaces the answer.
  void pollForReply(cid);
}

/** Show the server transcript once its last message is the assistant's reply.
 *  Before that it changes nothing, so the optimistic user message survives. */
export async function applyReplyIfDone(orgId: string, cid: string): Promise<boolean> {
  const res = await api.getConversation(orgId, cid);
  if (!chatSending.value || chatActiveId.value !== cid) return false; // answered or moved on meanwhile
  if (pendingConversationId !== null && pendingConversationId !== cid) return false; // another chat is opening
  if (!res.ok || res.data === null) return false;
  const msgs = res.data.messages;
  if (msgs.length === 0 || msgs[msgs.length - 1]?.role !== 'assistant') return false;
  chatMessages.value = msgs;
  chatReadOnly.value = res.data.readOnly;
  chatSending.value = false;
  chatLiveActivity.value = '';
  return true;
}

/** Poll intervals for the chat.done fallback: quick at first, then slower, for
 *  about a minute so a busy worker still surfaces its reply (HS2-ZVVES3). */
export const REPLY_POLL_DELAYS_MS: readonly number[] = [
  ...Array.from({ length: 20 }, () => 800),
  ...Array.from({ length: 25 }, () => 2000),
];

export async function pollForReply(cid: string): Promise<void> {
  const orgId = me.value?.organizationId;
  if (orgId === null || orgId === undefined) return;
  const waiting = (): boolean => chatSending.value && chatActiveId.value === cid;
  for (const delay of REPLY_POLL_DELAYS_MS) {
    if (!waiting()) break;
    await new Promise((r) => setTimeout(r, delay));
    if (!waiting()) break;
    if (await applyReplyIfDone(orgId, cid)) return;
  }
  // Out of time, or the user moved to another chat: stop waiting so they can send again.
  chatSending.value = false;
  chatLiveActivity.value = '';
}

export async function chatNewConversation(): Promise<void> {
  const orgId = me.value?.organizationId;
  const target = chatTarget.value;
  if (orgId === null || orgId === undefined || target === null) return;
  const res = await api.createConversation(orgId, target);
  if (res.ok && res.data !== null) {
    chatHistoryOpen.value = false;
    await loadConversation(res.data.id);
    await loadChat();
  }
}

export async function applyChatCard(spec: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const [messageId, idxRaw] = spec.split(':');
  if (orgId === null || orgId === undefined || messageId === undefined || idxRaw === undefined) return;
  const res = await api.applyChatCard(orgId, chatActiveId.value, messageId, Number(idxRaw));
  if (res.ok) {
    await loadConversation(chatActiveId.value);
    if (chatTarget.value?.scope === 'demand') await refreshWorkspace();
    else await refreshLibrary(); // a library fact was accepted — refresh the facts list
  } else notice.value = res.error ?? 'Could not apply the action.';
}

export async function dismissChatCard(spec: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const [messageId, idxRaw] = spec.split(':');
  if (orgId === null || orgId === undefined || messageId === undefined || idxRaw === undefined) return;
  const res = await api.dismissChatCard(orgId, chatActiveId.value, messageId, Number(idxRaw));
  if (res.ok) await loadConversation(chatActiveId.value);
}
